import React, { useEffect, useRef, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { shortSku } from '../utils/sku';
import { toast } from 'sonner';
import {
  BookOpen, PlayCircle, Image, Search, FileText, Upload, AlertTriangle,
  Sparkles, Wand2, Eye, LayoutDashboard, TrendingUp, ExternalLink, RotateCw,
  CheckCircle2, X
} from 'lucide-react';

async function downloadYujuSheet(jobId, accion = 'Agregar') {
  try {
    const { data } = await api.get(
      `/publications/yuju-images/${jobId}.xlsx?accion=${accion}`, { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Actualizacion_de_imagenes.xlsx';
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Archivo listo para subir a Yuju');
  } catch (err) {
    // A blob response carries the error as a blob too, so it has to be read.
    let detail = 'No se pudo armar el archivo';
    try { detail = JSON.parse(await err.response?.data?.text?.() || '{}').detail || detail; } catch { /* keep default */ }
    toast.error(detail);
  }
}

async function mintLinks(jobId) {
  // Yuju takes images by URL, not by upload, so the seller needs public links
  // to hand over. They are signed and expire on their own.
  try {
    const { data } = await api.post(`/publications/image-links/${jobId}?days=14`);
    const text = data.productos
      .map(p => [p.sku || p.producto || '', ...p.urls].join('\n'))
      .join('\n\n');
    await navigator.clipboard.writeText(text);
    const count = data.productos.reduce((n, p) => n + p.urls.length, 0);
    toast.success(`${count} enlaces copiados · expiran ${new Date(data.expira).toLocaleDateString()}`);
  } catch (err) {
    toast.error(err.response?.data?.detail || 'No se pudieron generar los enlaces');
  }
}

function BatchImagesPanel() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [estado, setEstado] = useState('todas');
  const [stock, setStock] = useState('todas');
  const [marca, setMarca] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [optimizationFilters, setOptimizationFilters] = useState({ seo: 'todas', descripcion: 'todas', imagenes: 'todas', ficha: 'todas' });
  const [listaId, setListaId] = useState('');
  const [picked, setPicked] = useState({});
  const [mode, setModeRaw] = useState('completa');
  const [programado, setProgramado] = useState(false);
  const [quality, setQuality] = useState('low');
  const [selectedLayouts, setSelectedLayouts] = useState(defaultLayouts('completa'));
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, step: '' });
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [batches, setBatches] = useState([]);
  const [espacio, setEspacio] = useState(null);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchesError, setBatchesError] = useState('');
  const [reviewSummaries, setReviewSummaries] = useState({});

  // Plantilla has seven concepts, completa has eight (it adds panorama_360,
  // which only means anything when the AI actually paints a synthetic
  // angle) - resetting the selection on every mode switch keeps whichever
  // catalogue is showing in sync with what is actually selected.
  const catalogue = LAYOUT_SETS[mode] || LAYOUT_SETS.plantilla;
  const setMode = m => {
    setModeRaw(m);
    setSelectedLayouts(LAYOUT_SETS[m].map(([k]) => k));
    if (m !== 'completa') setProgramado(false);
  };
  const toggleLayout = key => setSelectedLayouts(
    selectedLayouts.includes(key) ? selectedLayouts.filter(k => k !== key) : [...selectedLayouts, key]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bulk/products', {
        params: { search: query, estado, stock, marca, proveedor, ...optimizationFilters, publicacion: 'publicadas', lista_id: listaId || undefined, page, limit: BULK_PAGE_SIZE },
      });
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar tus publicaciones');
    } finally {
      setLoading(false);
    }
  }, [query, estado, stock, marca, proveedor, optimizationFilters, listaId, page]);

  // Typing is not a search: waiting out the pause keeps it to one query per
  // word rather than one per keystroke.
  React.useEffect(() => {
    const timer = setTimeout(load, query ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, query]);

  React.useEffect(() => { setPage(1); }, [query, estado, stock, marca, proveedor, optimizationFilters, listaId]);

  const chosen = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

  // The estimate comes from the server so the cap and the rates have one home.
  React.useEffect(() => {
    if (!chosen.length) return setEstimate(null);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/publications/batch-images/estimate', {
          item_ids: chosen, mode, quality, programado,
          layouts_json: JSON.stringify(selectedLayouts),
        });
        if (!cancelled) setEstimate(data);
      } catch { /* the button reports the cap on submit anyway */ }
    })();
    return () => { cancelled = true; };
  }, [chosen.length, mode, quality, programado, selectedLayouts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = products;
  const pages = Math.max(1, Math.ceil(total / BULK_PAGE_SIZE));
  const firstShown = total ? (page - 1) * BULK_PAGE_SIZE + 1 : 0;
  const lastShown = Math.min(page * BULK_PAGE_SIZE, total);

  // A queued batch finishes on OpenAI's clock and nothing here is awake to
  // notice, so every look at the list also pokes the ones still queued -
  // that GET is what checks OpenAI and starts collecting a finished one.
  const loadBatches = React.useCallback(async (announce = false) => {
    setBatchesLoading(true);
    setBatchesError('');
    try {
      const { data } = await api.get('/publications/batch-images');
      const lotes = data.lotes || [];
      setBatches(lotes);
      setEspacio(data.espacio || null);
      const summaries = await Promise.all(lotes.filter(b => b.status === 'completed').map(b =>
        api.get(`/publications/batch-images/${b.job_id}/review`)
          .then(r => [b.job_id, r.data]).catch(() => null)));
      setReviewSummaries(current => ({ ...current, ...Object.fromEntries(summaries.filter(Boolean)) }));

      const queued = lotes.filter(b => b.status === 'en_cola');
      if (!queued.length) return lotes;

      const checked = await Promise.all(queued.map(b =>
        api.get(`/publications/batch-images/${b.job_id}`)
          .then(r => r.data).catch(() => null)));
      // Collecting takes a couple of minutes, so a batch that just landed
      // reads as "processing" here and as "completed" on the next look.
      if (checked.some(j => j && j.status !== 'en_cola')) {
        const { data: fresh } = await api.get('/publications/batch-images');
        setBatches(fresh.lotes || []);
      }
      if (announce) {
        const ready = checked.filter(j => j && j.status === 'completed').length;
        if (ready) toast.success(`${ready} lote(s) programado(s) ya están listos`);
      }
      return lotes;
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'No se pudieron actualizar los lotes.';
      setBatchesError(message);
      if (announce) toast.error(message);
    } finally {
      setBatchesLoading(false);
    }
  }, []);
  React.useEffect(() => { loadBatches(); }, [loadBatches]);

  const copyReviewLink = async (id) => {
    try {
      const { data } = await api.post(`/publications/batch-images/${id}/review-link`, {});
      await navigator.clipboard.writeText(`${window.location.origin}${data.url || `/revision/${data.token}`}`);
      toast.success('Link de revisión copiado');
      const { data: summary } = await api.get(`/publications/batch-images/${id}/review`);
      setReviewSummaries(current => ({ ...current, [id]: summary }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear el link de revisión');
    }
  };

  // While something is queued, keep looking on the seller's behalf - they
  // asked to be told when the images are done, not to come back and check.
  const queuedCount = batches.filter(b => b.status === 'en_cola').length;
  React.useEffect(() => {
    if (!queuedCount) return;
    const timer = setInterval(() => loadBatches(true), 60000);
    return () => clearInterval(timer);
  }, [queuedCount, loadBatches]);

  /** Waits only for the handover to OpenAI, which takes a minute at most. */
  const waitUntilQueued = async (id) => {
    for (let i = 0; i < 120; i++) {
      await new Promise(r => setTimeout(r, 3000));
      const { data } = await api.get(`/publications/batch-images/${id}`);
      setProgress({ done: data.procesados || 0, total: data.total || chosen.length,
                    step: data.step || 'Preparando...' });
      if (data.status === 'en_cola') return data;
      if (data.status === 'error') throw new Error(data.error || 'El lote falló');
      if (data.status === 'completed') return data;
    }
    throw new Error('La preparación tardó demasiado. Revisa "Lotes recientes".');
  };

  /** Opens a finished batch, or nudges a queued one into being collected. */
  const openBatch = async (id) => {
    try {
      const { data } = await api.get(`/publications/batch-images/${id}`);
      if (data.status === 'completed' && data.result?.items?.length) {
        setJobId(id);
        setResult(data.result);
        return;
      }
      if (data.status === 'error') return toast.error(data.error || 'Ese lote falló');
      toast.info(data.step || 'Todavía en la cola de OpenAI');
      await loadBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo abrir el lote');
    }
  };

  const run = async () => {
    if (!chosen.length) return toast.error('Selecciona al menos una publicación');
    if (!selectedLayouts.length) return toast.error('Selecciona al menos una infografía.');
    setRunning(true);
    setResult(null);
    setProgress({ done: 0, total: chosen.length, step: 'En cola...' });
    try {
      const { data } = await api.post('/publications/batch-images', {
        item_ids: chosen, mode, quality, programado,
        layouts_json: JSON.stringify(selectedLayouts),
      });
      setJobId(data.job_id);

      if (programado) {
        // Nothing to wait for: once OpenAI has the queue, the answer comes
        // hours later. Poll only until it is handed over, then let go.
        await waitUntilQueued(data.job_id);
        await loadBatches();
        setPicked({});
        toast.success('Lote enviado a la cola. Lo verás en "Lotes recientes" cuando esté listo.');
        return;
      }

      const done = await pollJob('/publications/batch-images', data.job_id, null, job =>
        setProgress({ done: job.procesados || 0, total: job.total || chosen.length, step: job.step || '' }));
      setResult(done);
      toast.success(`${done.imagenes} imágenes en ${done.total} publicaciones`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error en el lote');
    } finally {
      setRunning(false);
    }
  };

  const downloadZip = async () => {
    try {
      const { data } = await api.get(`/publications/batch-images/${jobId}/zip`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'infografias-lote.zip';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo armar el ZIP');
    }
  };

  const cap = estimate?.maximo_por_lote || (mode === 'completa' ? 10 : 50);
  const overCap = chosen.length > cap;
  const chosenOption = (estimate?.opciones_costo || []).find(o => o.quality === quality);

  // Picking a filtered catalogue one checkbox at a time is the slow part:
  // the seller filters to a brand and wants that whole set. Asked for `cap`
  // rather than everything, because more than that cannot be run anyway -
  // this fills the batch to the brim and says what is left for the next one.
  const selectAllFiltered = async () => {
    try {
      const { data } = await api.get('/bulk/products', {
        params: { search: query, estado, stock, marca, proveedor, ...optimizationFilters, publicacion: 'publicadas', lista_id: listaId || undefined, page: 1, limit: cap },
      });
      const ids = (data.products || []).map(p => p.meli_id).filter(Boolean);
      if (!ids.length) return toast.error('No hay publicaciones que seleccionar');
      setPicked(Object.fromEntries(ids.map(id => [id, true])));
      const matching = data.total ?? ids.length;
      toast.success(matching > ids.length
        ? `${ids.length} seleccionadas de ${matching} — el máximo por lote en este modo`
        : `${ids.length} publicaciones seleccionadas`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron seleccionar');
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-center text-sm text-slate-400 max-w-xl mx-auto">
        Genera imágenes de varias publicaciones de una vez. Las fotos salen de cada
        publicación, no hace falta subirlas. Hasta {cap} por lote en este modo.
      </p>

      {!result && (
        <div className="max-w-2xl mx-auto space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={() => setMode('plantilla')}
              className={`text-left p-4 rounded-xl border transition-colors ${
                mode === 'plantilla' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600'}`}>
              <p className="text-sm font-semibold text-white">Plantillas · sin costo</p>
              <p className="text-xs text-slate-400 mt-1">Hasta 50 publicaciones por lote.</p>
            </button>
            <button onClick={() => setMode('completa')}
              className={`text-left p-4 rounded-xl border transition-colors ${
                mode === 'completa' ? 'border-amber-500 bg-amber-500/10' : 'border-slate-700 hover:border-slate-600'}`}>
              <p className="text-sm font-semibold text-white">Composición completa · en prueba</p>
              <p className="text-xs text-slate-400 mt-1">
                Hasta {BATCH_COMPLETA_MAX} publicaciones por lote. La IA arma cada
                imagen entera en una pasada y revisa el texto antes de guardarla.
              </p>
            </button>
          </div>

          {mode === 'completa' && (
            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              programado ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 hover:border-slate-600'}`}>
              <input type="checkbox" checked={programado}
                onChange={e => setProgramado(e.target.checked)}
                className="accent-emerald-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-white">
                  Programado · se procesa aunque cierres la página
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  MeliHub las procesa desde una cola persistente aunque cierres la página.
                  Misma calidad y precio normal de Images API, hasta {BATCH_COMPLETA_MAX}
                  publicaciones por lote. El lote te espera en "Lotes recientes".
                </p>
              </div>
            </label>
          )}

          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-xs text-slate-400">¿Cuáles de las {catalogue.length} quieres generar?</p>
              <button
                onClick={() => setSelectedLayouts(
                  selectedLayouts.length === catalogue.length ? [] : catalogue.map(([k]) => k))}
                className="text-xs text-blue-400 hover:text-blue-300">
                {selectedLayouts.length === catalogue.length ? 'Ninguna' : 'Todas'}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {catalogue.map(([key, name]) => {
                const Icon = LAYOUT_ICONS[key] || Sparkles;
                const on = selectedLayouts.includes(key);
                return (
                  <button key={key} onClick={() => toggleLayout(key)}
                    className={`text-left p-2.5 rounded-lg border transition-colors ${
                      on ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600 opacity-60'}`}>
                    <div className="flex items-center gap-1.5">
                      <Icon size={14} className="text-blue-400 shrink-0" />
                      <p className="text-[11px] font-medium text-white leading-tight">{name}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-600 mt-1.5">
              Cada publicación recibe también su Foto Principal gratuita cuando la foto de referencia permite construirla (fondo blanco, 1024x1024px).
            </p>
          </div>

          {mode === 'completa' && (estimate?.opciones_costo || []).length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {estimate.opciones_costo.map(option => (
                <button key={option.quality} onClick={() => setQuality(option.quality)}
                  className={`p-2.5 rounded-lg border text-center transition-colors ${
                    quality === option.quality ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600'}`}>
                  <p className="text-xs font-semibold text-white">{option.etiqueta}</p>
                  <p className="text-[11px] text-emerald-400 mt-0.5">${option.costo_usd} USD</p>
                </button>
              ))}
            </div>
          )}

          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar por título, SKU, MLM o marca..."
            className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <FilterRow options={SHEET_FILTERS.estado} value={estado} onChange={setEstado} />
            <FilterRow options={SHEET_FILTERS.stock} value={stock} onChange={setStock} />
            <OptimizationFilters values={optimizationFilters} onChange={(field, value) => setOptimizationFilters(f => ({ ...f, [field]: value }))} />
            <CatalogueFilters marca={marca} setMarca={setMarca}
              proveedor={proveedor} setProveedor={setProveedor} />
            <ListaFiltro listaId={listaId} setListaId={setListaId} />
            <button onClick={selectAllFiltered}
              title={`Selecciona hasta ${cap} de las publicaciones filtradas, el máximo por lote en este modo`}
              className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800">
              Seleccionar filtradas
            </button>
            {chosen.length > 0 && (
              <button onClick={() => setPicked({})}
                className="px-3 py-1.5 border border-slate-700 text-slate-400 rounded-lg text-xs hover:bg-slate-800">
                Limpiar ({chosen.length})
              </button>
            )}
          </div>
          {loading ? (
            <p className="text-center text-sm text-slate-500 py-6">Cargando publicaciones...</p>
          ) : !visible.length ? (
            <p className="text-center text-sm text-slate-500 py-6">
              {query || estado !== 'todas' || stock !== 'todas' || marca || proveedor || listaId || Object.values(optimizationFilters).some(v => v !== 'todas')
                ? 'Ninguna publicación coincide con esos filtros.'
                : 'No hay publicaciones. Corre una sincronización primero.'}
            </p>
          ) : (
            <>
            <p className="text-xs text-slate-500">
              Mostrando {firstShown}–{lastShown} de {total} publicaciones
              {chosen.length > 0 && (
                <span className="text-blue-400"> · {chosen.length} seleccionadas en total</span>
              )}
            </p>
            <div className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
              {visible.map(product => (
                <label key={product.meli_id}
                  className={`flex items-center gap-3 rounded-lg p-2.5 border cursor-pointer ${
                    picked[product.meli_id] ? 'border-blue-500/50 bg-blue-500/5' : 'border-slate-800/50 bg-slate-900/50'}`}>
                  <input type="checkbox" checked={!!picked[product.meli_id]}
                    onChange={e => setPicked(p => ({ ...p, [product.meli_id]: e.target.checked }))}
                    className="accent-blue-500 shrink-0" />
                  {product.image && (
                    <img src={product.image} alt="" className="w-10 h-10 rounded object-cover bg-slate-800 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{product.name || product.meli_id}</p>
                    <ItemIds product={product} />
                    {product.marca && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-300">
                        {product.marca}
                      </span>
                    )}
                    <OptimizationBadges product={product} />
                  </div>
                </label>
              ))}
            </div>
            {pages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  Anterior
                </button>
                <span className="text-xs text-slate-500">Página {page} de {pages}</span>
                <button
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  disabled={page >= pages}
                  className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  Siguiente
                </button>
              </div>
            )}
            </>
          )}

          <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm text-white">
                {chosen.length} publicaciones · {(estimate?.imagenes ?? chosen.length * selectedLayouts.length)} imágenes
              </p>
              <p className="text-xs text-slate-500">
                {mode === 'completa' ? (
                  <>
                    Costo estimado:{' '}
                    <span className="text-emerald-400">
                      ${chosenOption?.costo_usd ?? estimate?.costo_estimado_usd ?? 0} USD
                    </span>
                    {' '}en tu cuenta de OpenAI
                  </>
                ) : 'Sin costo de OpenAI'}
                {overCap && <span className="text-amber-400"> · máximo {cap} por lote</span>}
              </p>
            </div>
            <button onClick={run} disabled={running || !chosen.length || overCap || !selectedLayouts.length}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50">
              {running ? (programado ? 'Enviando...' : 'Generando...')
                       : `${programado ? 'Programar' : 'Generar'} lote de ${chosen.length}`}
            </button>
          </div>

          {running && (
            <div>
              <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all"
                     style={{ width: `${(progress.done / (progress.total || 1)) * 100}%` }} />
              </div>
              <p className="text-center text-xs text-slate-400 mt-2">{progress.step}</p>
            </div>
          )}

          {batches.length > 0 && (
            <div>
              {espacio?.pct >= 70 && (
                <p className={`text-xs mb-2 px-3 py-2 rounded-lg border ${
                  espacio.pct >= 85 ? 'border-red-500/40 bg-red-500/10 text-red-300'
                                    : 'border-amber-500/40 bg-amber-500/10 text-amber-300'}`}>
                  Base de datos al {espacio.pct}% ({espacio.usado_mb} de {espacio.limite_mb} MB).
                  Las imágenes generadas son lo que más ocupa: descarga los ZIP que necesites
                  {espacio.pct >= 85 && ' — al llenarse, MeliHub deja de guardar ventas y sincronizaciones'}.
                </p>
              )}
              <div className="flex items-baseline justify-between mb-2">
                <p className="text-xs text-slate-400">Lotes recientes</p>
                <button onClick={() => loadBatches(true)} disabled={batchesLoading}
                        className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50">
                  {batchesLoading ? 'Actualizando...' : 'Actualizar'}
                </button>
              </div>
              {batchesError && <p className="mb-2 text-xs text-red-300">{batchesError}</p>}
              <div className="space-y-1.5">
                {batches.map(b => (
                  <div key={b.job_id} className="w-full flex items-center gap-3 rounded-lg p-2.5 border border-slate-800/50 bg-slate-900/50 hover:border-slate-700">
                  <button onClick={() => openBatch(b.job_id)}
                    className="w-full text-left flex items-center gap-3 rounded-lg p-2.5 border border-slate-800/50 bg-slate-900/50 hover:border-slate-700">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
                      b.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400'
                        : b.status === 'error' ? 'bg-red-500/15 text-red-400'
                        : b.status === 'en_cola' ? 'bg-amber-500/15 text-amber-400'
                        : 'bg-slate-700/50 text-slate-400'}`}>
                      {b.status === 'completed' ? 'Listo'
                        : b.status === 'error' ? 'Falló'
                        : b.status === 'en_cola' ? 'En cola' : 'Procesando'}
                    </span>
                    <span className="text-xs text-slate-300 truncate flex-1">
                      {b.total} publicaciones
                      {b.programado && <span className="text-emerald-500/70"> · programado</span>}
                      {b.result?.imagenes > 0 && ` · ${b.result.imagenes} imágenes`}
                      {b.images_pruned && <span className="text-slate-600"> · imágenes ya borradas</span>}
                    </span>
                    <span className="text-[11px] text-slate-600 shrink-0">
                      {b.created_at && new Date(b.created_at).toLocaleString('es-MX',
                        { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </button>
                  {b.status === 'completed' && <div className="flex shrink-0 items-center gap-2">
                    {reviewSummaries[b.job_id] && <span className="hidden text-[10px] text-slate-500 sm:inline">✓ {reviewSummaries[b.job_id].totales?.ok || 0} · ✕ {reviewSummaries[b.job_id].totales?.mal || 0} · {reviewSummaries[b.job_id].totales?.quitar || 0} fuera · {reviewSummaries[b.job_id].totales?.sin_calificar || 0} pendientes</span>}
                    <button onClick={() => copyReviewLink(b.job_id)} className="rounded-md border border-emerald-500/30 px-2 py-1 text-[11px] font-medium text-emerald-300 hover:bg-emerald-500/10">Copiar link</button>
                  </div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-bold text-white">
                {result.imagenes} imágenes en {result.total} publicaciones
              </h3>
              {result.costo_estimado_usd > 0 && (
                <p className="text-xs text-slate-500">Costo estimado ${result.costo_estimado_usd} USD</p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => copyReviewLink(jobId)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium">
                Copiar link de revisión
              </button>
              <button onClick={downloadZip}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium">
                Descargar ZIP
              </button>
              <button onClick={() => downloadYujuSheet(jobId)}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-800">
                Excel para Yuju
              </button>
              <button onClick={() => mintLinks(jobId)}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-800">
                Enlaces para Yuju
              </button>
              <button onClick={() => { setResult(null); setPicked({}); }}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-800">
                Nuevo lote
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {result.items.map(row => (
              <div key={row.item_id} className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
                <p className="text-sm text-white truncate">{row.producto || row.titulo}</p>
                <ItemIds product={row} className="mb-2" />
                {row.error ? (
                  <p className="text-xs text-red-400">{row.error}</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                    {row.images.map(image => (
                      <img key={image.key} src={image.url} alt={image.name}
                           title={image.name}
                           className="w-full aspect-square object-cover rounded border border-slate-800" />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const LAYOUT_ICONS = {
  lifestyle: Wand2, beneficio: FileText, tecnica: LayoutDashboard,
  comparativa: TrendingUp, tamano: Eye, branding: BookOpen, campana: Sparkles,
  panorama_360: RotateCw,
};

// Plantilla draws its seven for free with Pillow over the product's own
// photo - there is no AI call to ask for a synthetic angle, so a "360 view"
// would just be the same photo again. Completa paints the whole frame with
// the full model, which is what panorama_360 actually needs to mean anything.
const SEVEN_LAYOUTS = [
  ['lifestyle', 'Lifestyle principal'], ['beneficio', 'Beneficio principal'],
  ['tecnica', 'Características técnicas'], ['comparativa', 'Comparativa'],
  ['tamano', 'Tamaño y contenido'], ['branding', 'Branding y confianza'],
  ['campana', 'Imagen publicitaria'],
];
const EIGHT_LAYOUTS = [...SEVEN_LAYOUTS, ['panorama_360', 'Vista 360 panorámica']];
const LAYOUT_SETS = { plantilla: SEVEN_LAYOUTS, completa: EIGHT_LAYOUTS };
// Espeja el default del backend; panorama_360 queda disponible, pero no preseleccionado.
const DEFAULT_LAYOUT_KEYS = ['lifestyle', 'beneficio', 'tecnica', 'comparativa', 'tamano', 'campana'];
const defaultLayouts = mode => (LAYOUT_SETS[mode] || [])
  .filter(([key]) => DEFAULT_LAYOUT_KEYS.includes(key)).map(([key]) => key);

function PhotoFixPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get('/photo-fix');
      const visible = (data.jobs || []).filter(job =>
        ['pendiente_aprobacion', 'no_tocar', 'corregir_en_yuju'].includes(job.resultado));
      const unique = new Map();
      visible.forEach(job => { if (!unique.has(job.item_id)) unique.set(job.item_id, job); });
      setJobs([...unique.values()]);
    } catch (err) {
      // A disconnected account should not hide the normal image generator.
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const decide = async (job, action) => {
    setBusy(current => ({ ...current, [job.job_id]: true }));
    try {
      const { data } = await api.post(`/photo-fix/${job.job_id}/${action}`);
      setJobs(current => current.filter(item => item.job_id !== job.job_id));
      toast.success(action === 'approve' && data?.resultado !== 'no_tocar'
        ? 'Foto subida y verificada en Mercado Libre' : 'Corrección descartada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo procesar la corrección');
    } finally {
      setBusy(current => ({ ...current, [job.job_id]: false }));
    }
  };

  if (loading || !jobs.length) return null;
  return (
    <section className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-slate-900/70 to-slate-950 p-4 sm:p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-amber-300/80">Revisión pendiente</p>
          <h2 className="text-xl font-semibold text-white mt-1">Fotos por corregir <span className="text-amber-300">· {jobs.length}</span></h2>
          <p className="text-xs text-slate-400 mt-1">Aprueba sólo los cambios que quieras subir. Mercado Libre volverá a moderar la publicación.</p>
        </div>
        <button onClick={load} className="text-xs text-slate-400 hover:text-white">Actualizar</button>
      </div>
      <div className="grid gap-3">
        {jobs.map(job => {
          const after = job.imagenes_generadas?.[0]?.url;
          if (job.resultado === 'corregir_en_yuju') {
            const reason = { catalogo: 'Catálogo', inactiva: 'Inactiva', pausada: 'Pausada', cerrada: 'Cerrada' }[job.resultado_detalle] || job.resultado_detalle || 'No editable';
            return (
              <article key={job.job_id} className="rounded-xl border border-amber-500/30 bg-slate-950/50 p-3">
                <p className="text-sm text-white truncate">{job.titulo || job.item_id}</p>
                <p className="text-[11px] text-amber-300 mt-1">Corregir en Yuju (por SKU) · {reason}</p>
                <p className="text-[11px] text-slate-500 mt-1">SKU: {job.sku || 'sin SKU'} · {job.item_id}</p>
              </article>
            );
          }
          if (job.resultado === 'no_tocar') {
            return (
              <article key={job.job_id} className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                <p className="text-sm text-white truncate">{job.titulo}</p>
                <p className="text-[11px] text-red-300 mt-1">{job.motivo} · No se modificó la publicación</p>
                <p className="text-[11px] text-slate-500 mt-1">{job.item_id} · Revisión manual requerida</p>
              </article>
            );
          }
          return (
            <article key={job.job_id} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/50 p-3">
              <div>
                <p className="text-sm text-white truncate" title={job.titulo}>{job.titulo}</p>
                <p className="text-[11px] text-amber-300 mt-1">{job.motivo} · {job.tipo === 'agregar' ? 'Agregar fotos' : 'Reemplazar principal'}</p>
                <p className="text-[11px] text-slate-500 mt-1">{job.item_id}</p>
              </div>
              <div className="flex gap-2 items-center">
                <div className="text-center"><span className="block text-[10px] uppercase tracking-wider text-slate-600 mb-1">Antes</span><img src={job.source_image_url} alt="Antes" className="w-16 h-16 object-cover rounded-lg border border-slate-800" /></div>
                <span className="text-slate-600">→</span>
                <div className="text-center"><span className="block text-[10px] uppercase tracking-wider text-slate-600 mb-1">Después</span><img src={after} alt="Después" className="w-16 h-16 object-cover rounded-lg border border-amber-500/40" /></div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => decide(job, 'discard')} disabled={busy[job.job_id]} title="Descartar"
                  className="p-2 rounded-lg border border-slate-700 text-slate-400 hover:text-red-300 hover:border-red-500/40 disabled:opacity-50"><X size={16} /></button>
                <button onClick={() => decide(job, 'approve')} disabled={busy[job.job_id]} title="Aprobar y subir"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium disabled:opacity-50"><CheckCircle2 size={15} /> Aprobar y subir</button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ImageGeneratorTab({ seoAnalysis, onGoToSeo }) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [sku, setSku] = useState('');
  const [extraInfo, setExtraInfo] = useState('');
  const [brandFile, setBrandFile] = useState(null);

  const [analysis, setAnalysis] = useState(null);
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState('completa');
  const [useOriginal, setUseOriginal] = useState(false);
  // gpt-image-2 low is the default; medium/high remain available when the
  // seller wants to trade more spend for a different render tier.
  const [quality, setQuality] = useState('low');
  const [selectedLayouts, setSelectedLayouts] = useState(defaultLayouts('completa'));

  const [busy, setBusy] = useState(false);
  const [stepText, setStepText] = useState('');
  const [layouts, setLayouts] = useState([]);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const [scope, setScope] = useState('uno');
  const hasSeo = Boolean(seoAnalysis);

  const loadHistory = React.useCallback(async () => {
    try {
      const { data } = await api.get('/publications/image-history');
      setHistory(data.generations || []);
    } catch {
      // History is a convenience; failing to load it shouldn't block anything.
    }
  }, []);
  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  const addFiles = React.useCallback((incoming) => {
    const images = Array.from(incoming || []).filter(f => f.type?.startsWith('image/'));
    if (!images.length) return;
    const oversized = images.find(f => f.size > 10 * 1024 * 1024);
    if (oversized) return toast.error(`${oversized.name} supera los 10MB`);
    setFiles(prev => {
      if (prev.length + images.length > 5) toast.info('Máximo 5 fotos por generación');
      return [...prev, ...images].slice(0, 5);
    });
  }, []);

  // Object URLs have to be revoked or the tab leaks a blob per preview.
  React.useEffect(() => {
    const urls = files.map(f => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach(URL.revokeObjectURL);
  }, [files]);

  // The upload box promises Ctrl+V, so it has to actually listen for it.
  React.useEffect(() => {
    const onPaste = e => {
      const pasted = Array.from(e.clipboardData?.files || []);
      if (pasted.length) addFiles(pasted);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [addFiles]);

  const handleFileDrop = e => { e.preventDefault(); addFiles(e.dataTransfer?.files || e.target.files); };

  const handleAnalyze = async () => {
    if (!files.length) return toast.error('Sube al menos una foto');
    setBusy(true);
    setStepText('Enviando las fotos...');
    try {
      const form = new FormData();
      files.forEach(f => form.append('files', f));
      if (sku.trim()) form.append('sku', sku.trim());
      if (extraInfo) form.append('extra_info', extraInfo);
      if (brandFile) form.append('brand_manual', brandFile);
      if (hasSeo) {
        form.append('use_session_data', 'true');
        form.append('product_name', seoAnalysis.query_used || '');
        form.append('category', seoAnalysis.categoria_nombre || '');
        form.append('keywords_json', JSON.stringify(seoAnalysis.palabras_clave_ordenadas || []));
      }
      const { data } = await api.post('/publications/analyze-product', form);
      const done = await pollJob('/publications/analysis-job', data.job_id, setStepText);
      setAnalysis(done);
      setAnalysisJobId(data.job_id);
      toast.success('Análisis listo: revísalo antes de generar');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error al analizar el producto');
    } finally {
      setBusy(false);
      setStepText('');
    }
  };

  const pickMode = (m) => {
    setMode(m);
    setSelectedLayouts(defaultLayouts(m));
  };

  const handleGenerate = async () => {
    const chosen = LAYOUT_SETS[mode].filter(([k]) => selectedLayouts.includes(k));
    if (chosen.length === 0) {
      toast.error('Selecciona al menos una infografía.');
      return;
    }
    setBusy(true);
    setResult(null);
    setStepText('Preparando...');
    setLayouts(chosen.map(([key, nombre]) => ({ key, nombre, estado: 'pendiente' })));
    try {
      const form = new FormData();
      form.append('analysis_job_id', analysisJobId);
      form.append('analysis_json', JSON.stringify(analysis.analisis));
      form.append('mode', mode);
      if (useOriginal) form.append('usar_original', 'true');
      form.append('layouts_json', JSON.stringify(chosen.map(([k]) => k)));
      form.append('quality', quality);

      const { data } = await api.post('/publications/generate-images', form);
      const done = await pollJob('/publications/image-job', data.job_id, setStepText,
                                 job => setLayouts(job.layouts || []));
      setResult({ ...done, job_id: data.job_id });
      toast.success('Infografías generadas');
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error al generar infografías');
    } finally {
      setBusy(false);
      setStepText('');
    }
  };

  const openGeneration = async jobId => {
    try {
      const { data } = await api.get(`/publications/image-job/${jobId}`);
      if (!data.result?.images?.length) return toast.error('Esta generación ya no conserva las imágenes.');
      setResult({ ...data.result, job_id: jobId });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo abrir la generación');
    }
  };

  const slug = (result?.producto || 'producto').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);

  const download = image => {
    const link = document.createElement('a');
    link.href = image.url;
    link.download = `${slug}-${image.key}.jpg`;
    link.click();
  };

  const downloadZip = async () => {
    try {
      // The endpoint needs the auth header, so a plain link cannot fetch it.
      const { data } = await api.get(`/publications/image-zip/${result.job_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${slug}-infografias.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo armar el ZIP');
    }
  };

  const setField = (key, value) =>
    setAnalysis(a => ({ ...a, analisis: { ...a.analisis, [key]: value } }));

  const step = result ? 3 : layouts.length && busy ? 2 : analysis ? 1 : 0;
  const steps = [
    { label: 'Subir Producto', icon: Upload },
    { label: 'Análisis IA', icon: Sparkles },
    { label: 'Generar Infografías', icon: Image },
    { label: 'Resultado', icon: Eye },
  ];
  const ready = layouts.filter(l => l.estado === 'listo').length;

  return (
    <div className="space-y-6">
      <PhotoFixPanel />
      <div className="flex items-center justify-center gap-2">
        {[['uno', 'Un producto'], ['lote', 'Por lotes']].map(([id, label]) => (
          <button key={id} onClick={() => setScope(id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              scope === id ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-300'}`}>
            {label}
          </button>
        ))}
      </div>

      {scope === 'lote' && <BatchImagesPanel />}

      {scope === 'uno' && (<>
      <div className="flex items-center justify-center gap-0 flex-wrap">
        {steps.map((s, i) => (
          <React.Fragment key={s.label}>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
              i <= step ? 'bg-blue-600/20 text-blue-400' : 'text-slate-500'
            }`}>
              <s.icon size={15} />
              {s.label}
            </div>
            {i < steps.length - 1 && <div className="w-12 h-[2px] bg-slate-700 mx-1" />}
          </React.Fragment>
        ))}
      </div>

      {/* ---------------------------------------------------------- upload */}
      {!analysis && !result && (
        <>
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-3">Generador de Imágenes para MeLi</h2>
            <p className="text-sm text-slate-400 max-w-xl mx-auto">
              Sube una o varias fotos y generaremos <span className="text-blue-400 font-medium">infografías</span> con
              textos optimizados. Primero verás el análisis para corregirlo antes de generar.
            </p>
          </div>

          {hasSeo ? (
            <p className="text-center text-xs text-emerald-400">
              Usando los datos del Optimizador SEO ({seoAnalysis.query_used})
            </p>
          ) : (
            <p className="text-center text-xs text-slate-500">
              Sin datos del{' '}
              <button onClick={onGoToSeo} className="text-blue-400 hover:text-blue-300 underline">Optimizador SEO</button>
              : los textos saldrán de la foto y de lo que escribas abajo.
            </p>
          )}

          <div
            data-testid="image-upload-area"
            onDragOver={e => e.preventDefault()}
            onDrop={handleFileDrop}
            onClick={() => document.getElementById('pub-file-input')?.click()}
            className="border-2 border-dashed border-slate-700 rounded-xl p-10 text-center hover:border-blue-500/50 transition-colors cursor-pointer mx-auto max-w-xl"
          >
            <Upload size={40} className="text-slate-500 mx-auto mb-4" />
            <p className="text-base font-semibold text-white mb-1">Arrastra, pega o haz clic</p>
            <p className="text-xs text-slate-500 mb-2">PNG, JPG o WEBP — Máximo 10MB</p>
            <p className="text-xs text-blue-400">Hasta 5 fotos: se reparten entre las infografías</p>
            <p className="text-xs text-slate-600 mt-2 flex items-center justify-center gap-1">
              <FileText size={12} /> También puedes pegar con Ctrl+V
            </p>
            <input id="pub-file-input" type="file" multiple accept="image/*" onChange={handleFileDrop} className="hidden" />
          </div>

          {files.length > 0 && (
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="relative">
                  <img src={previews[i]} alt={f.name} className="w-24 h-24 object-cover rounded-lg border border-slate-700" />
                  <button
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-900 border border-slate-700 text-slate-400 hover:text-red-400 text-sm leading-none"
                  >×</button>
                </div>
              ))}
            </div>
          )}

          <div className="max-w-xl mx-auto space-y-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1.5">SKU del producto (opcional)</label>
              <input
                value={sku}
                onChange={e => setSku(e.target.value)}
                placeholder="Ej: LG-30CAP-001"
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500"
              />
              <p className="text-xs text-slate-600 mt-1">
                Yuju indexa por SKU: sin él no se puede armar el Excel de imágenes.
              </p>
            </div>

            <div>
              <label className="block text-xs text-slate-400 mb-1.5">Información adicional del producto (opcional)</label>
              <textarea
                value={extraInfo}
                onChange={e => setExtraInfo(e.target.value)}
                rows={3}
                placeholder="Ej: Auriculares Bluetooth con cancelación de ruido, 40 horas de batería, resistentes al agua IPX5..."
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500"
              />
              <p className="text-xs text-slate-600 mt-1">
                Medidas, materiales y contenido de la caja sólo se afirman si los escribes aquí.
              </p>
            </div>

            <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <BookOpen size={16} className="text-amber-400 shrink-0" />
                  <span className="text-sm font-semibold text-white truncate">
                    {brandFile ? brandFile.name : 'Manual de Marca'}
                  </span>
                </div>
                <label className="px-3 py-1.5 border border-slate-700 rounded-lg text-xs text-slate-300 hover:bg-slate-800 cursor-pointer shrink-0">
                  {brandFile ? 'Cambiar' : 'Subir marca'}
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 8 * 1024 * 1024) return toast.error('El manual debe pesar menos de 8MB');
                      setBrandFile(f);
                    }}
                  />
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                PDF o imagen. Se leen los colores y el tono para que las infografías respeten tu identidad.
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-center text-sm font-bold text-white mb-3">
              Las {LAYOUT_SETS.plantilla.length} infografías que generaremos
            </h3>
            <p className="text-center text-xs text-slate-500 -mt-2 mb-3">
              La Vista 360 se suma si eliges Composición completa en el siguiente paso.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mx-auto">
              {LAYOUT_SETS.plantilla.map(([key, name], i) => {
                const Icon = LAYOUT_ICONS[key] || Sparkles;
                return (
                  <div key={key} className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4 text-center">
                    <Icon size={18} className="text-blue-400 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-white">{i + 1}. {name}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {files.length > 0 && (
            <div className="text-center">
              <button
                onClick={handleAnalyze}
                disabled={busy}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50"
              >
                {busy ? (stepText || 'Analizando...') : 'Analizar Producto'}
              </button>
            </div>
          )}

          {history.length > 0 && !busy && (
            <div className="max-w-xl mx-auto">
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Mis últimas generaciones</p>
              <div className="space-y-1.5">
                {history.slice(0, 6).map(gen => (
                  <button
                    key={gen.job_id}
                    onClick={() => gen.images_available && openGeneration(gen.job_id)}
                    disabled={!gen.images_available}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-800/40 border border-slate-700/50 rounded-lg text-sm text-left hover:border-blue-500/40 disabled:opacity-50"
                  >
                    <span className="text-slate-300 truncate">{gen.producto}</span>
                    <span className="text-xs text-slate-500 shrink-0 ml-3">
                      {gen.images_available ? new Date(gen.created_at).toLocaleDateString() : 'imágenes depuradas'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* -------------------------------------------------------- analysis */}
      {analysis && !result && !(busy && layouts.length > 0) && (
        <AnalysisReview
          analysis={analysis}
          editing={editing}
          setEditing={setEditing}
          setField={setField}
          mode={mode}
          setMode={pickMode}
          useOriginal={useOriginal}
          setUseOriginal={setUseOriginal}
          selectedLayouts={selectedLayouts}
          setSelectedLayouts={setSelectedLayouts}
          quality={quality}
          setQuality={setQuality}
          busy={busy}
          onGenerate={handleGenerate}
          onBack={() => { setAnalysis(null); setAnalysisJobId(null); }}
        />
      )}

      {/* ------------------------------------------------------ generating */}
      {busy && layouts.length > 0 && !result && (
        <div className="space-y-5">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-1">Generando Infografías</h2>
            <p className="text-sm text-slate-400">{stepText || 'Creando tus infografías...'}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mx-auto">
            {layouts.map((l, i) => (
              <div key={l.key} className={`rounded-xl p-4 text-center border ${
                l.estado === 'listo' ? 'border-emerald-500/40 bg-emerald-500/5'
                : l.estado === 'generando' ? 'border-blue-500/60 bg-blue-500/10'
                : l.estado === 'error' ? 'border-red-500/40 bg-red-500/5'
                : 'border-slate-800/50 bg-slate-900/50'}`}>
                <p className="text-xs font-semibold text-white">{i + 1}. {l.nombre}</p>
                <p className="text-[11px] mt-1 text-slate-400">
                  {l.estado === 'listo' ? 'Lista' : l.estado === 'generando' ? 'Generando...'
                    : l.estado === 'error' ? 'No se pudo' : 'En espera'}
                </p>
              </div>
            ))}
          </div>
          <div className="max-w-lg mx-auto">
            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all"
                   style={{ width: `${(ready / (layouts.length || 1)) * 100}%` }} />
            </div>
            <p className="text-center text-xs text-slate-500 mt-2">{ready} de {layouts.length} imágenes</p>
          </div>
        </div>
      )}

      {/* ----------------------------------------------------------- result */}
      {result && (
        <div className="space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-2xl font-bold text-white">{result.total} imágenes generadas</h2>
              <p className="text-sm text-slate-400">
                {result.producto}
                {result.costo_estimado_usd > 0 && ` · costo estimado $${result.costo_estimado_usd} USD`}
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={downloadZip}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium">
                Descargar ZIP
              </button>
              <button onClick={() => downloadYujuSheet(result.job_id)}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-800">
                Excel para Yuju
              </button>
              <button onClick={() => mintLinks(result.job_id)}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-800">
                Enlaces para Yuju
              </button>
              <button
                onClick={() => { setResult(null); setAnalysis(null); setAnalysisJobId(null); setFiles([]); setLayouts([]); }}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-800">
                Nuevo Producto
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {result.images.map((image, i) => (
              <div key={image.key} className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
                <img src={image.url} alt={image.name} className="w-full aspect-square object-cover" />
                <div className="flex items-center justify-between px-3 py-2.5">
                  <span className="text-sm text-slate-300 truncate">{i + 1}. {image.name}</span>
                  <button onClick={() => download(image)} className="text-xs text-blue-400 hover:text-blue-300 shrink-0 ml-2">
                    Descargar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

function EditableList({ label, rows, onChange }) {
  return (
    <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
      <p className="text-xs text-slate-400 mb-2">{label}</p>
      <div className="space-y-1.5">
        {(rows || []).map((row, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="text-blue-400 text-xs mt-1.5">›</span>
            {onChange ? (
              <input
                value={row}
                onChange={e => onChange(rows.map((r, j) => (j === i ? e.target.value : r)))}
                className="flex-1 bg-slate-800/60 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
              />
            ) : (
              <span className="text-sm text-slate-300">{row}</span>
            )}
          </div>
        ))}
        {!(rows || []).length && <p className="text-sm text-slate-600">—</p>}
      </div>
    </div>
  );
}

function AnalysisReview({ analysis, editing, setEditing, setField, mode, setMode,
                         quality, setQuality, busy, onGenerate, onBack,
                         useOriginal, setUseOriginal,
                         selectedLayouts, setSelectedLayouts }) {
  const catalogue = LAYOUT_SETS[mode] || LAYOUT_SETS.plantilla;
  const toggleLayout = key => setSelectedLayouts(
    selectedLayouts.includes(key) ? selectedLayouts.filter(k => k !== key) : [...selectedLayouts, key]);
  const a = analysis.analisis || {};
  const originals = analysis.originales || [];
  // Completa always sends the original to the model regardless of this
  // checkbox - it composes its own backdrop, so a cutout is not even useful
  // to it. The preview has to agree, or picking that mode looks like it did
  // nothing.
  const willUseOriginal = mode === 'completa' || useOriginal;
  const photos = (willUseOriginal && originals.length ? originals : analysis.fotos) || [];
  const brand = analysis.marca || {};

  const fields = [
    ['nombre', 'Nombre'], ['categoria', 'Categoría'], ['frase_gancho', 'Frase gancho'],
    ['color_principal', 'Color principal'], ['material', 'Material'],
    ['uso_principal', 'Uso principal'], ['estilo', 'Estilo'],
  ];

  // Plantillas are free; only completa has a price table. The principal uses
  // the existing free path, so the quote scales over selected shots only.
  const scale = selectedLayouts.length / (catalogue.length || 8);
  const options = (analysis.opciones_costo_completa || [])
    .map(o => ({ ...o, etiqueta: o.quality === 'low' ? 'Estándar' : o.etiqueta,
      costo_usd: Math.round(o.costo_usd * scale * 1000) / 1000 }));
  const chosen = options.find(o => o.quality === quality) || options[0] || { costo_usd: 0 };

  const flat = key => (a[key] || []).map(r =>
    typeof r === 'object' ? `${r.nombre || r.pregunta || ''}: ${r.valor || r.respuesta || ''}` : String(r));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-white">Análisis del Producto</h2>
        <div className="flex gap-2">
          <button onClick={onBack} className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800">
            Volver
          </button>
          <button onClick={() => setEditing(!editing)}
            className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800">
            {editing ? 'Listo' : 'Editar'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
          <p className="text-sm font-semibold text-white mb-3">
            Imagen del producto {willUseOriginal || !analysis.fondo_removido ? '(fondo original)' : '(sin fondo)'}
          </p>
          {photos[0] && (
            <img src={photos[0]} alt="Producto"
                 className="w-full rounded-lg bg-slate-800/60 object-contain max-h-80" />
          )}
          {photos.length > 1 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {photos.slice(1).map((p, i) => (
                <img key={i} src={p} alt="" className="w-16 h-16 object-contain rounded bg-slate-800/60" />
              ))}
            </div>
          )}
          {!analysis.fondo_removido && (
            <p className="text-xs text-amber-400/80 mt-2">
              El fondo no era liso, así que se dejó intacto para no dañar la foto.
            </p>
          )}
          {analysis.fondo_removido && originals.length > 0 && mode !== 'completa' && (
            <label className="flex items-start gap-2 mt-3 cursor-pointer">
              <input type="checkbox" checked={useOriginal}
                     onChange={e => setUseOriginal(e.target.checked)}
                     className="mt-0.5 accent-blue-500" />
              <span className="text-xs text-slate-400">
                Usar la foto original. Márcalo si al quitar el fondo se comió
                un pedazo del producto: las infografías saldrán con la foto
                completa tal como la subiste.
              </span>
            </label>
          )}
          {analysis.fondo_removido && mode === 'completa' && (
            <p className="text-xs text-slate-500 mt-3">
              Este modo compone su propia escena, así que siempre usa la foto
              tal como la subiste, con fondo y todo.
            </p>
          )}
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4 space-y-3">
          {fields.map(([key, label]) => (
            <div key={key}>
              <p className="text-xs text-slate-500">{label}</p>
              {editing ? (
                <input
                  value={a[key] || ''}
                  onChange={e => setField(key, e.target.value)}
                  className="w-full bg-slate-800/60 border border-slate-700 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-500"
                />
              ) : (
                <p className="text-sm text-white">{a[key] || 'N/A'}</p>
              )}
            </div>
          ))}
          {brand.colores?.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-1">Colores de tu marca</p>
              <div className="flex gap-2">
                {brand.colores.map(c => (
                  <span key={c} className="w-6 h-6 rounded border border-slate-700" style={{ background: c }} title={c} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EditableList label="Características" rows={flat('caracteristicas')} />
        <EditableList label="Beneficios" rows={a.beneficios}
                      onChange={editing ? v => setField('beneficios', v) : null} />
        <EditableList label="Diferenciadores" rows={a.diferenciadores}
                      onChange={editing ? v => setField('diferenciadores', v) : null} />
        <EditableList label="Objeciones comunes" rows={flat('objeciones')} />
        <EditableList label="Dimensiones" rows={flat('dimensiones')} />
        <EditableList label="Se entrega con" rows={a.incluye}
                      onChange={editing ? v => setField('incluye', v) : null} />
      </div>

      <div className="max-w-2xl mx-auto space-y-3">
        <p className="text-xs text-slate-400 text-center">¿Cómo quieres las imágenes?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => setMode('plantilla')}
            className={`text-left p-4 rounded-xl border transition-colors ${
              mode === 'plantilla' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600'}`}>
            <p className="text-sm font-semibold text-white">Plantillas · sin costo</p>
            <p className="text-xs text-slate-400 mt-1">
              Tu foto sobre diseños con tus colores. Textos siempre nítidos.
            </p>
          </button>
          <button
            onClick={() => setMode('completa')}
            className={`text-left p-4 rounded-xl border transition-colors ${
              mode === 'completa' ? 'border-amber-500 bg-amber-500/10' : 'border-slate-700 hover:border-slate-600'}`}>
            <p className="text-sm font-semibold text-white">
              Composición completa · desde ${((analysis.opciones_costo_completa || [])[0] || {}).costo_usd} USD
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Siete infografías distintas —lifestyle, beneficio, técnica, comparativa,
              tamaño, campaña y vista 360— más una Foto Principal, armadas
              por la IA en una pasada. Cada imagen se revisa y se reintenta una vez si el texto no coincide.
            </p>
          </button>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-xs text-slate-400">¿Cuáles de las {catalogue.length} quieres generar?</p>
            <button
              onClick={() => setSelectedLayouts(
                selectedLayouts.length === catalogue.length ? [] : catalogue.map(([k]) => k))}
              className="text-xs text-blue-400 hover:text-blue-300">
              {selectedLayouts.length === catalogue.length ? 'Ninguna' : 'Todas'}
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {catalogue.map(([key, name]) => {
              const Icon = LAYOUT_ICONS[key] || Sparkles;
              const on = selectedLayouts.includes(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleLayout(key)}
                  className={`text-left p-2.5 rounded-lg border transition-colors ${
                    on ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600 opacity-60'}`}>
                  <div className="flex items-center gap-1.5">
                    <Icon size={14} className="text-blue-400 shrink-0" />
                    <p className="text-[11px] font-medium text-white leading-tight">{name}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {mode === 'completa' && (
          <div className="grid grid-cols-3 gap-2">
            {options.map(option => (
              <button
                key={option.quality}
                onClick={() => setQuality(option.quality)}
                className={`p-2.5 rounded-lg border text-center transition-colors ${
                  quality === option.quality ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600'}`}>
                <p className="text-xs font-semibold text-white">{option.etiqueta}</p>
                <p className="text-[11px] text-emerald-400 mt-0.5">${option.costo_usd} USD</p>
              </button>
            ))}
          </div>
        )}

        <button
          onClick={onGenerate}
          disabled={busy || selectedLayouts.length === 0}
          className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold text-sm disabled:opacity-50">
          {busy ? 'Generando...' :
            `Generar ${selectedLayouts.length} Infografía${selectedLayouts.length === 1 ? '' : 's'}` +
            (mode !== 'plantilla' ? ` · ~$${chosen.costo_usd} USD` : '')}
        </button>
      </div>
    </div>
  );
}

// Analysis runs as a background job on the server, so the UI polls until it
// finishes instead of holding a request open long enough to time out.
const POLL_MS = 4000;
// Seven AI-composed scenes take a couple of minutes; the old three-minute
// ceiling would abandon a job that was still working.
const MAX_POLL_MS = 600000;
// A batch is a different animal: fifty listings, three optimisations each,
// every one of them an AI call. Judging that by the same clock as a single
// analysis is what made a job that was working perfectly well report a
// failure to the seller.
const MAX_BATCH_POLL_MS = 3600000;
// Twelve seconds of silence is not a broken server, it is a small instance
// having a moment. What matters is whether the job stops making progress.
const NETWORK_STRIKES = 8;

async function pollJob(endpoint, jobId, onStep, onJob, maxMs = MAX_POLL_MS) {
  const startedAt = Date.now();
  let networkErrors = 0;

  while (Date.now() - startedAt < maxMs) {
    await new Promise(r => setTimeout(r, POLL_MS * (networkErrors ? 2 : 1)));
    try {
      const { data } = await api.get(`${endpoint}/${jobId}`);
      onJob?.(data);
      if (data.status === 'completed') return data.result;
      if (data.status === 'error') throw new Error(data.error || 'El análisis falló');
      onStep?.(data.step || 'Procesando...');
      networkErrors = 0;
    } catch (err) {
      // A failed poll is usually a hiccup, not a failed job — but a real
      // error message from the server should surface immediately.
      if (err.message && !err.response) {
        if (++networkErrors >= NETWORK_STRIKES) {
          throw new Error('Se perdió la conexión con el servidor');
        }
      } else if (err.response?.status === 404) {
        throw new Error('El análisis expiró. Intenta de nuevo.');
      } else if (!err.response) {
        throw err;
      }
    }
  }
  throw new Error('El análisis tardó demasiado. Intenta de nuevo.');
}

const RELEVANCE_STYLES = {
  Alta: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  Media: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  Baja: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const TREND_STYLES = {
  Alta: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  Media: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  Baja: 'bg-orange-500/10 text-orange-300/80 border-orange-500/20',
};

const TREND_DOT = {
  Alta: 'bg-emerald-400',
  Media: 'bg-amber-400',
  Baja: 'bg-orange-400',
};

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
  toast.success('Copiado al portapapeles');
}

function TitleCard({ index, title, limit, catalog }) {
  const pct = Math.min(100, Math.round((title.length / limit) * 100));
  const bar = pct >= 85 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-slate-600';

  return (
    <div className="bg-slate-800/50 rounded-lg px-4 py-3">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-5 h-5 shrink-0 rounded bg-slate-700 text-[11px] font-bold text-slate-300 flex items-center justify-center">
            {index}
          </span>
          {catalog && (
            <span className="shrink-0 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 text-[10px] font-semibold">
              Catálogo
            </span>
          )}
          <span className="text-xs text-slate-500 shrink-0">{title.length}/{limit} caracteres</span>
        </div>
        <button onClick={() => copyToClipboard(title)} className="text-xs text-blue-400 hover:text-blue-300 shrink-0">
          Copiar
        </button>
      </div>
      <p className="text-sm text-slate-100 mb-2">{title}</p>
      <div className="h-1 rounded-full bg-slate-700/60 overflow-hidden">
        <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SEOOptimizerTab({ onAnalysis }) {
  const [productName, setProductName] = useState('');
  const [url, setUrl] = useState('');
  const [image, setImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [step, setStep] = useState('');
  const [result, setResult] = useState(null);

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('La imagen debe ser menor a 5MB');
    setImage(file);
  };

  const handleAnalyze = async () => {
    if (!productName && !url && !image) {
      return toast.error('Ingresa un nombre, una URL o una imagen');
    }
    setAnalyzing(true);
    setResult(null);
    setStep('Iniciando análisis...');
    try {
      const form = new FormData();
      if (productName) form.append('product_name', productName);
      if (url) form.append('ml_url', url);
      if (image) form.append('image', image);

      const { data } = await api.post('/seo/analyze', form);
      const analysis = await pollJob('/seo/job-status', data.job_id, setStep);
      setResult(analysis);
      onAnalysis?.(analysis);
      toast.success('Análisis completado');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error al analizar');
    } finally {
      setAnalyzing(false);
      setStep('');
    }
  };

  const titleLen = result?.titulo_actual_chars || 0;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-white mb-2">Optimizador de Títulos SEO</h2>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto">
          Extrae intención de búsqueda <span className="text-blue-400 font-medium">real de Mercado Libre</span> para
          generar títulos optimizados que maximicen la visibilidad de tu producto.
        </p>
      </div>

      {/* Every input is optional on its own, so they are grouped and labelled
          rather than stacked as three competing "or" placeholders. */}
      <div className="max-w-5xl mx-auto bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <Search size={18} className="text-blue-400" />
          <h3 className="text-lg font-semibold text-white">Origen de Datos</h3>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Puedes proporcionar uno, varios o todos los datos para mejor precisión
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="flex items-center gap-1.5 text-xs text-slate-400 mb-2">
              <FileText size={13} /> Nombre del Producto
            </label>
            <input
              data-testid="seo-name-input"
              type="text"
              placeholder="Ej: Gorro satín para dormir"
              value={productName}
              onChange={e => setProductName(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-600"
            />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-xs text-slate-400 mb-2">
              <ExternalLink size={13} /> URL de Mercado Libre (Opcional)
            </label>
            <input
              data-testid="seo-url-input"
              type="text"
              placeholder="https://articulo.mercadolibre.com.mx/..."
              value={url}
              onChange={e => setUrl(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-600"
            />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-xs text-slate-400 mb-2">
              <Image size={13} /> Foto del Producto
            </label>
            <label className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-colors">
              <Upload size={15} className="text-slate-400" />
              <span className="text-sm text-slate-400 truncate">
                {image ? image.name : 'Subir imagen'}
              </span>
              <input type="file" accept="image/*" onChange={handleImage} className="hidden" data-testid="seo-image-upload" />
            </label>
          </div>
        </div>

        <div className="flex justify-center mt-6">
          <button
            data-testid="seo-analyze-btn"
            onClick={handleAnalyze}
            disabled={analyzing}
            className="flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-base font-semibold shadow-lg shadow-blue-500/20 disabled:opacity-50 transition-all"
          >
            <Sparkles size={18} />
            {analyzing ? (step || 'Analizando...') : 'Extraer Palabras Clave y Generar Títulos'}
          </button>
        </div>
      </div>

      {/* Empty state: say what the module produces instead of leaving a blank
          page under the form. */}
      {!result && !analyzing && (
        <div className="text-center py-12">
          <Search size={44} className="text-slate-700 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Extrae intención de búsqueda real</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Ingresa el nombre de tu producto, una URL de Mercado Libre, o sube una foto
            para obtener palabras clave basadas en datos reales de búsqueda.
          </p>
        </div>
      )}

      {result && (
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="flex items-center justify-center gap-2 flex-wrap text-xs">
            <span className="px-3 py-1 rounded-full bg-slate-800/60 border border-slate-700/50 text-slate-400">
              Tendencias: {(result.tendencias || []).length}
            </span>
            <span className="px-3 py-1 rounded-full bg-slate-800/60 border border-slate-700/50 text-slate-400">
              Competencia: {result.mercado?.listados_analizados || 0} listados
            </span>
            {result.categoria_nombre && (
              <span className="px-3 py-1 rounded-full bg-slate-800/60 border border-slate-700/50 text-slate-400">
                Categoría: {result.categoria_nombre}
              </span>
            )}
          </div>

          {(result.avisos || []).map((aviso, i) => (
            <p key={i} className="text-xs text-amber-400 text-center">{aviso}</p>
          ))}

          {(result.tendencias || []).length > 0 && (
            <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-1">
                Más Buscados en Categoría{' '}
                <span className="text-slate-500 font-normal">({result.tendencias.length} tendencias)</span>
              </h3>
              <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-500 mb-2">
                {[['Alta', 'cat. general'], ['Media', 'cat. padre'], ['Baja', 'cat. específica']].map(([vol, label]) => (
                  <span key={vol} className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${TREND_DOT[vol]}`} />
                    {vol} ({label})
                  </span>
                ))}
              </div>
              {result.categoria_ruta?.length > 0 && (
                <p className="text-[11px] text-slate-500 mb-3">
                  Categorías analizadas: {result.categoria_ruta.join(' → ')}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {result.tendencias.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => copyToClipboard(t.keyword)}
                    className={`px-3 py-1 rounded-full border text-xs font-medium ${TREND_STYLES[t.volumen] || TREND_STYLES.Baja}`}
                  >
                    {t.keyword}
                  </button>
                ))}
              </div>
            </div>
          )}

          {result.titulo_actual && (
            <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-white">Tu título actual</h3>
                <span className={`text-xs font-medium ${titleLen > 60 ? 'text-red-400' : 'text-slate-400'}`}>
                  {titleLen}/60 caracteres
                </span>
              </div>
              <p className="text-sm text-slate-300">{result.titulo_actual}</p>
            </div>
          )}

          {/* Keywords beside the titles they produced, as Selltrix arranges them:
              the seller reads the words first and the titles built from them second. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white">
                Keywords Filtrados por IA{' '}
                <span className="text-slate-500 font-normal">({(result.palabras_clave_ordenadas || []).length})</span>
              </h3>
              <p className="text-xs text-slate-500 mb-3">Sólo los que aplican a tu producto</p>
              <div className="flex flex-wrap gap-2">
                {(result.palabras_clave_ordenadas || []).map((k, i) => {
                  const word = typeof k === 'object' ? k.palabra : k;
                  const rel = typeof k === 'object' ? k.relevancia : 'Media';
                  return (
                    <button
                      key={i}
                      onClick={() => copyToClipboard(word)}
                      className={`px-3 py-1 rounded-full border text-xs font-medium ${RELEVANCE_STYLES[rel] || RELEVANCE_STYLES.Baja}`}
                    >
                      {word}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <div className="flex items-baseline justify-between mb-1">
                <h3 className="text-sm font-semibold text-white">Títulos Optimizados</h3>
                {result.diagnostico && (
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(result.diagnostico, null, 2))}
                    className="text-xs text-slate-500 hover:text-slate-300"
                    title="Copia lo que decidió cada paso del análisis, para reportar un resultado raro"
                  >
                    Copiar diagnóstico
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-500 mb-3">
                {(result.titulos_publicacion || []).length} títulos para publicación (60 car.)
                {result.titulo_catalogo ? ' + 1 para catálogo (120 car.)' : ''}
              </p>
              <div className="space-y-2">
                {(result.titulos_publicacion || []).map((t, i) => (
                  <TitleCard key={i} index={i + 1} title={t} limit={60} />
                ))}
                {result.titulo_catalogo && (
                  <TitleCard index={(result.titulos_publicacion || []).length + 1}
                             title={result.titulo_catalogo} limit={120} catalog />
                )}
              </div>
            </div>
          </div>

          {result.mercado?.listados_analizados > 0 && (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
                <p className="text-xs text-slate-400 mb-1">Listados analizados</p>
                <p className="text-xl font-bold text-white">{result.mercado.listados_analizados}</p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
                <p className="text-xs text-slate-400 mb-1">Precio promedio</p>
                <p className="text-xl font-bold text-white">
                  ${Math.round(result.mercado.precio_promedio).toLocaleString('es-MX')}
                </p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
                <p className="text-xs text-slate-400 mb-1">Unidades vendidas</p>
                <p className="text-xl font-bold text-white">
                  {result.mercado.unidades_vendidas?.toLocaleString('es-MX')}
                </p>
              </div>
            </div>
          )}

          {result.keywords_competencia?.some(k => !k.en_tu_titulo) && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-amber-400 mb-1">
                Palabras que usa la competencia y tú no
              </h3>
              <p className="text-xs text-slate-400 mb-3">
                Ordenadas por cuántos de los listados analizados las incluyen.
              </p>
              <div className="space-y-2">
                {result.keywords_competencia
                  .filter(k => !k.en_tu_titulo)
                  .slice(0, 8)
                  .map(k => (
                    <div key={k.palabra} className="flex items-center gap-3">
                      <span className="text-sm text-slate-200 w-32 shrink-0">{k.palabra}</span>
                      <div className="flex-1 bg-slate-800 rounded-full h-2">
                        <div className="h-2 rounded-full bg-amber-500" style={{ width: `${k.cobertura_pct}%` }} />
                      </div>
                      <span className="text-xs text-slate-500 w-12 text-right">
                        {Math.round(k.cobertura_pct)}%
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Every section the generator writes, in the order it writes them. Warranty
// and trial periods are deliberately absent: the seller does not offer them,
// and promising them in the listing commits them to it.
const DESCRIPTION_SECTIONS = [
  'Títulos SEO Secundarios', '¿Qué es el producto?', 'Acerca de este producto',
  'Gancho persuasivo', 'Características', 'Ventajas',
  'Se entrega con (Accesorios)', 'Por qué elegirnos', 'Preguntas frecuentes',
];

const KEYWORDS_SHOWN = 8;

function timeAgo(timestamp) {
  if (!timestamp) return '';
  const minutes = Math.floor((Date.now() - timestamp) / 60000);
  if (minutes < 1) return 'recién';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `hace ${hours} h` : `hace ${Math.floor(hours / 24)} d`;
}

function DescriptionTab({ seoAnalysis, onGoToSeo }) {
  const [productInfo, setProductInfo] = useState('');
  const [image, setImage] = useState(null);
  const [useSeoData, setUseSeoData] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [step, setStep] = useState('');
  const [result, setResult] = useState(null);
  const [draft, setDraft] = useState('');
  const [manualMode, setManualMode] = useState(false);

  const hasSeo = Boolean(seoAnalysis);
  const keywordWords = (seoAnalysis?.palabras_clave_ordenadas || [])
    .map(k => (typeof k === 'object' ? k.palabra : k))
    .filter(Boolean);

  // Re-read on a timer, so a card that says "hace 2 min" is not still saying
  // it twenty minutes later.
  const [analysedAgo, setAnalysedAgo] = useState(timeAgo(seoAnalysis?.analizado_en));
  useEffect(() => {
    setAnalysedAgo(timeAgo(seoAnalysis?.analizado_en));
    const timer = setInterval(() => setAnalysedAgo(timeAgo(seoAnalysis?.analizado_en)), 30000);
    return () => clearInterval(timer);
  }, [seoAnalysis]);

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('La imagen debe ser menor a 5MB');
    setImage(file);
  };

  const handleGenerate = async () => {
    const name = productInfo || seoAnalysis?.query_used || '';
    if (!name && !image) return toast.error('Describe tu producto o sube una imagen');

    setGenerating(true);
    setResult(null);
    setStep('Enviando solicitud...');
    try {
      const form = new FormData();
      form.append('product_name', name);
      // The seller's own notes are the only source for what comes in the
      // box; without them that section is left out entirely.
      if (productInfo) form.append('extra_details', productInfo);
      if (image) form.append('image', image);

      const reuse = hasSeo && useSeoData;
      form.append('use_session_data', reuse ? 'true' : 'false');
      if (reuse) {
        form.append('keywords_json', JSON.stringify(seoAnalysis.palabras_clave_ordenadas || []));
        form.append('titles_json', JSON.stringify(seoAnalysis.sugerencias_titulos || []));
        form.append('category', seoAnalysis.categoria_nombre || seoAnalysis.category_detected || '');
        // What Mercado Libre actually confirmed about the product, so the
        // description states only these and treats keywords as search terms.
        form.append('attributes_json', JSON.stringify(seoAnalysis.atributos_detectados || {}));
      }

      const { data } = await api.post('/descriptions/generate', form);
      const generated = await pollJob('/descriptions/job-status', data.job_id, setStep);
      setResult(generated);
      setDraft(generated.description || '');
      toast.success('Descripción generada');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error al generar descripción');
    } finally {
      setGenerating(false);
      setStep('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-white mb-2">Generador de Descripciones</h2>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto">
          Genera descripciones <span className="text-blue-400 font-medium">estructuradas y optimizadas</span> para
          tus productos de Mercado Libre usando palabras clave SEO.
        </p>
      </div>

      {/* A description written without the SEO analysis targets different
          words than the optimised title, so offer that path first rather
          than letting the user start from a blank box by accident. */}
      {!hasSeo && !manualMode && (
        <div className="max-w-3xl mx-auto bg-amber-500/5 border border-amber-500/20 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-2.5 bg-amber-500/15 rounded-xl shrink-0">
              <AlertTriangle size={20} className="text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white mb-1">No hay datos del Optimizador SEO</h3>
              <p className="text-sm text-slate-400 mb-4">
                Para generar una descripción optimizada, primero analiza un producto en la pestaña{' '}
                <button onClick={onGoToSeo} className="text-blue-400 hover:text-blue-300 underline">
                  Optimizador SEO
                </button>
                . Esto nos dará las palabras clave y títulos para crear una descripción más efectiva.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={onGoToSeo}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Sparkles size={15} />
                  Ir al Optimizador SEO →
                </button>
                <button
                  onClick={() => setManualMode(true)}
                  className="flex items-center gap-2 px-5 py-2.5 border border-slate-700 hover:bg-slate-800 text-slate-300 rounded-lg text-sm font-medium transition-colors"
                >
                  <FileText size={15} />
                  Ingresar datos manualmente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(hasSeo || manualMode) && (
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5 space-y-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
            <FileText size={15} className="text-blue-400" />
            Datos del Producto
          </h3>

          {/* What the description is about to be built from. Reading it back
              is how the seller catches an analysis of the wrong product
              before spending a generation on it. */}
          {hasSeo && useSeoData ? (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-emerald-500/15 rounded-lg shrink-0">
                  <Sparkles size={16} className="text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <h4 className="text-sm font-semibold text-white">
                      Usando datos del Optimizador SEO
                    </h4>
                    <span className="text-[11px] text-slate-500 shrink-0">{analysedAgo}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Producto: <span className="text-emerald-400">{seoAnalysis.query_used}</span>
                    {' • '}{keywordWords.length} palabras clave
                    {' • '}{(seoAnalysis.sugerencias_titulos || []).length} títulos
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {keywordWords.slice(0, KEYWORDS_SHOWN).map((word, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[11px] text-emerald-300/90">
                        {word}
                      </span>
                    ))}
                    {keywordWords.length > KEYWORDS_SHOWN && (
                      <span className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-[11px] text-slate-400">
                        +{keywordWords.length - KEYWORDS_SHOWN} más
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setUseSeoData(false)}
                    className="text-[11px] text-slate-500 hover:text-slate-300 underline mt-3"
                  >
                    Cambiar a modo manual
                  </button>
                </div>
              </div>
            </div>
          ) : hasSeo && (
            <button
              onClick={() => setUseSeoData(true)}
              className="w-full text-left bg-slate-800/40 border border-slate-700/60 rounded-xl px-4 py-3 text-xs text-slate-400 hover:text-slate-200"
            >
              Modo manual. <span className="underline">Volver a usar los datos del Optimizador SEO</span>
            </button>
          )}

          <textarea
            data-testid="description-input"
            placeholder={
              hasSeo && useSeoData
                ? 'Detalles adicionales (opcional): qué incluye la caja, medidas, materiales...'
                : 'Describe tu producto: nombre, características, beneficios, materiales...'
            }
            value={productInfo}
            onChange={e => setProductInfo(e.target.value)}
            rows={hasSeo && useSeoData ? 3 : 5}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 resize-none"
          />

          <div>
            <p className="text-xs text-slate-400 mb-1.5">
              Imagen del Producto <span className="text-slate-600">(opcional - enriquece la descripción)</span>
            </p>
            <label className="flex flex-col items-center justify-center gap-2 h-28 bg-slate-800/40 border border-slate-700 border-dashed rounded-xl cursor-pointer hover:bg-slate-800/70 transition-colors">
              <Image size={20} className="text-slate-500" />
              <span className="text-sm text-slate-400">
                {image ? image.name : 'Subir imagen'}
              </span>
              <input type="file" accept="image/*" onChange={handleImage} className="hidden" />
            </label>
            <p className="text-[11px] text-slate-600 mt-1.5">
              La IA extraerá características adicionales de la imagen
            </p>
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            <Sparkles size={16} />
            {generating ? (step || 'Generando...') : 'Generar Descripción'}
          </button>
        </div>

        {/* Named up front so nobody generates twice looking for a section
            that was never going to be there. */}
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-3">La descripción incluirá:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2">
            {DESCRIPTION_SECTIONS.map(name => (
              <span key={name} className="flex items-center gap-2 text-xs text-slate-400">
                <span className="w-1 h-1 rounded-full bg-blue-400 shrink-0" />
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>
      )}

      {result && (
        <div className="max-w-3xl mx-auto">
          <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-white">Descripción Generada</h3>
              <div className="flex gap-2">
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs font-medium hover:bg-slate-800 disabled:opacity-50"
                >
                  Regenerar
                </button>
                <button
                  onClick={() => copyToClipboard(draft)}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-medium"
                >
                  Copiar
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              {draft.length} caracteres · listo para pegar en Mercado Libre
            </p>
            {/* Editable and monospaced: the separators only line up in a
                fixed-width font, and that is how they read in the listing. */}
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              spellCheck={false}
              className="w-full h-[32rem] bg-slate-950/60 border border-slate-800 rounded-lg p-4 text-[13px] leading-relaxed text-slate-200 font-mono resize-y focus:outline-none focus:border-blue-500"
            />
            <p className="text-xs text-slate-600 mt-2">
              Puedes editarla antes de copiarla. Los separadores y encabezados son ajustables.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemIds({ product, className = '' }) {
  // SKU and the MLM id are how a seller finds a product in their own records;
  // the title alone is not enough when forty of them start with the same word.
  return (
    <span className={`text-[11px] text-slate-500 flex items-center gap-2 flex-wrap ${className}`}>
      {product.seller_sku && (
        <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
          SKU {shortSku(product.seller_sku)}
        </span>
      )}
      <span className="font-mono">{product.meli_id || product.item_id}</span>
    </span>
  );
}

const CONFIDENCE_STYLES = {
  alta: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  media: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  baja: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

// Mercado Libre grades a listing 0 to 1. The number is what the seller
// reads in their own panel, so it is shown as a number rather than a word.
function healthScore(salud) {
  const raw = salud?.health;
  if (raw == null) return null;
  // Mercado Libre reports it as 0 to 1. Guarding the other scale costs one
  // comparison and saves showing a seller "6500 de calidad".
  return Math.round(raw > 1 ? raw : raw * 100);
}

function healthTone(score) {
  return score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400';
}

// Beside the button that acts on it: the grade is the reason to open a
// listing, so it belongs where the eye already is when deciding.
function HealthScore({ salud }) {
  const score = healthScore(salud);
  if (score == null) {
    return (
      <span className="text-[10px] text-slate-600 text-center leading-tight w-12 shrink-0"
            title="Mercado Libre aún no reporta calidad para esta publicación">
        sin<br />calidad
      </span>
    );
  }
  return (
    <span className="text-center leading-none w-12 shrink-0"
          title="Calidad de la publicación según Mercado Libre">
      <span className={`text-lg font-bold ${healthTone(score)}`}>{score}</span>
      <span className="block text-[9px] text-slate-500 mt-0.5">calidad</span>
    </span>
  );
}

function HealthBadge({ salud, className = '' }) {
  const score = healthScore(salud);
  if (score == null) return null;
  const tone = score >= 80 ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
    : score >= 50 ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
    : 'bg-red-500/15 text-red-400 border-red-500/30';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tone} ${className}`}
          title="Calidad de la publicación según Mercado Libre">
      calidad {score}/100
    </span>
  );
}

// One attribute of the sheet, proposed or blank.
function AttributeRow({ sug, checked, onToggle, onChange }) {
  const filled = !!sug.value_name?.trim();
  return (
    <div className={`rounded-xl border p-3 ${
      checked ? 'border-blue-500/50 bg-blue-500/5' : 'border-slate-800/50 bg-slate-900/50'}`}>
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={checked && filled}
          disabled={!filled}
          onChange={e => onToggle(e.target.checked)}
          title={filled ? '' : 'Escribe un valor para poder aplicarlo'}
          className="accent-blue-500 shrink-0 disabled:opacity-40"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-white">{sug.nombre}</span>
            {sug.requerido && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">obligatorio</span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
              CONFIDENCE_STYLES[sug.confianza] || CONFIDENCE_STYLES.baja}`}>
              {sug.confianza ? `confianza ${sug.confianza}` : 'complétalo tú'}
            </span>
          </div>
          {sug.opciones?.length ? (
            <select
              value={sug.value_name}
              onChange={e => onChange(e.target.value)}
              className="mt-1.5 w-full bg-slate-800/60 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
            >
              {/* Without this an unproposed attribute would show the first
                  option as if it were chosen, while the value behind it is
                  still empty. */}
              <option value="">Elegir...</option>
              {sug.opciones.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input
              value={sug.value_name}
              onChange={e => onChange(e.target.value)}
              placeholder={sug.unidad ? `Valor en ${sug.unidad}` : 'Escribe el valor'}
              className="mt-1.5 w-full bg-slate-800/60 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Filters as the seller thinks of their catalogue, not as the database
// stores it.
const SHEET_FILTERS = {
  estado: [['todas', 'Todas'], ['activas', 'Activas'], ['inactivas', 'Inactivas']],
  stock: [['todas', 'Todo el stock'], ['con', 'Con stock'], ['sin', 'Sin stock']],
};

const SHEET_PAGE_SIZE = 20;

// Brand and supplier are the seller's own master data: Mercado Libre carries
// brand only as a per-listing attribute and has no notion of supplier, and
// Yuju's products-offer report covers stock and price per channel, never the
// product's own fields. They come from the same master sheet the seller
// already maintains, imported through /products/import-catalogo.
function useFilterOptions() {
  const [options, setOptions] = useState({ marcas: [], proveedores: [] });
  const [loaded, setLoaded] = useState(false);
  const refresh = React.useCallback(async () => {
    try {
      let { data } = await api.get('/attributes/filter-options');
      if (!(data.marcas || []).length && !(data.proveedores || []).length) {
        // First time: PriceHub already has brand and supplier per SKU.
        try {
          await api.post('/products/sync-pricehub-catalogo');
          ({ data } = await api.get('/attributes/filter-options'));
        } catch {
          // PriceHub unreachable - the selects just stay empty.
        }
      }
      setOptions({ marcas: data.marcas || [], proveedores: data.proveedores || [] });
    } catch {
      // Nothing on file yet - the selects just stay empty.
    } finally {
      setLoaded(true);
    }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);
  return { ...options, loaded, refresh };
}

function ListaFiltro({ listaId, setListaId }) {
  const [listas, setListas] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const inputId = React.useId();

  const cargar = React.useCallback(async () => {
    try {
      const { data } = await api.get('/listas');
      setListas(data.listas || []);
    } catch {
      setListas([]);
    }
  }, []);

  React.useEffect(() => { cargar(); }, [cargar]);

  const subir = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setSubiendo(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/listas', form, { params: { nombre: file.name } });
      await cargar();
      setListaId(data.id);
      // Lo que no cruzó importa más que lo que sí: es el momento en que
      // todavía se puede corregir el archivo.
      if (data.encontrados === 0) {
        toast.error(`Ninguno de los ${data.pedidos} códigos existe en tu catálogo`);
      } else if (data.total_sin_match > 0) {
        toast.warning(`${data.encontrados} de ${data.pedidos} encontrados · ${data.total_sin_match} sin coincidencia`);
      } else {
        toast.success(`${data.encontrados} publicaciones en la lista`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo leer el archivo');
    } finally {
      setSubiendo(false);
    }
  };

  const borrar = async () => {
    if (!listaId) return;
    try {
      await api.delete(`/listas/${listaId}`);
      setListaId('');
      await cargar();
    } catch {
      toast.error('No se pudo borrar la lista');
    }
  };

  return (
    <>
      {listas.length > 0 && (
        <select value={listaId} onChange={e => setListaId(e.target.value)}
          className="bg-slate-900/60 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500">
          <option value="">Sin lista</option>
          {listas.map(l => (
            <option key={l.id} value={l.id}>{l.nombre} ({l.tipo.toUpperCase()})</option>
          ))}
        </select>
      )}
      <label htmlFor={inputId}
        title="Sube un Excel o CSV con SKUs o MLMs para trabajar sólo ese lote"
        className={`px-3 py-1.5 border border-slate-700 rounded-lg text-xs cursor-pointer hover:bg-slate-800 ${
          subiendo ? 'opacity-50 pointer-events-none text-slate-500' : 'text-slate-300'}`}>
        {subiendo ? 'Leyendo...' : (listas.length ? '+ Lista' : 'Filtrar por lista')}
      </label>
      <input id={inputId} type="file"
             accept=".xlsx,.xlsm,.csv,.tsv,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
             className="hidden" onChange={subir} />
      {listaId && (
        <button onClick={borrar} title="Borrar esta lista"
          className="px-2 py-1.5 border border-slate-700 rounded-lg text-xs text-slate-400 hover:bg-slate-800">
          x
        </button>
      )}
    </>
  );
}

function CatalogueFilters({ marca, setMarca, proveedor, setProveedor }) {
  const { marcas, proveedores, loaded, refresh } = useFilterOptions();
  const [importing, setImporting] = useState(false);

  const importSheet = async () => {
    setImporting(true);
    try {
      const { data } = await api.post('/products/sync-pricehub-catalogo');
      await refresh();
      const missing = data.sin_coincidencia
        ? ` · ${data.sin_coincidencia} sin coincidencia en PriceHub (ej. ${(data.ejemplos_sin_coincidencia || []).slice(0, 3).join(', ')})`
        : '';
      const nuevos = data.sin_publicar?.creados
        ? ` · ${data.sin_publicar.creados} productos nuevos sin publicar traídos de PriceHub (filtro «Sin publicar»)`
        : '';
      toast.success((data.productos_actualizados > 0
        ? `${data.productos_actualizados} publicaciones actualizadas con marca y proveedor de PriceHub`
        : 'Marca y proveedor ya estaban al día con PriceHub') + nuevos + missing);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo leer PriceHub');
    } finally {
      setImporting(false);
    }
  };

  const uploader = (
    <button type="button" onClick={importSheet} disabled={importing}
      title="Trae marca y proveedor por SKU desde PriceHub, y los productos nuevos que aún no están publicados en ML"
      className={`px-3 py-1.5 border border-slate-700 rounded-lg text-xs hover:bg-slate-800 ${
        importing ? 'opacity-50 text-slate-500' : 'text-slate-300'}`}>
      {importing ? 'Leyendo PriceHub...' : (loaded && !marcas.length && !proveedores.length
        ? 'Traer marca y proveedor de PriceHub' : '↻ PriceHub')}
    </button>
  );

  // Nothing imported yet: the uploader is the one thing shown, instead of two
  // empty selects that look broken.
  if (loaded && !marcas.length && !proveedores.length) return uploader;

  return (
    <>
      {marcas.length > 0 && (
        <select value={marca} onChange={e => setMarca(e.target.value)}
          className="bg-slate-900/60 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500">
          <option value="">Toda marca</option>
          {marcas.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      )}
      {proveedores.length > 0 && (
        <select value={proveedor} onChange={e => setProveedor(e.target.value)}
          className="bg-slate-900/60 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500">
          <option value="">Todo proveedor</option>
          {proveedores.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      )}
      {uploader}
    </>
  );
}

// What "already done" means per listing: a real write MeliHub made, not a
// guess from title length or how full the sheet looks.
const OPTIMIZATION_FIELDS = [
  ['seo_aplicado_at', 'SEO'],
  ['descripcion_aplicada_at', 'Descripción'],
  ['imagenes_generadas_at', 'Imágenes'],
  ['ficha_aplicada_at', 'Ficha'],
];

const OPTIMIZATION_FILTERS = [
  ['seo', 'SEO'],
  ['descripcion', 'Descripción'],
  ['imagenes', 'Imágenes'],
  ['ficha', 'Ficha'],
];

const PUBLICACION_OPTIONS = [
  ['todas', 'Todas'], ['publicadas', 'Publicadas en ML'], ['sin_publicar', 'Sin publicar'],
];

function OptimizationFilters({ values, onChange, conPublicacion = false }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {conPublicacion && (
        <label className="flex items-center gap-1 text-[10px] text-slate-500">
          Publicación
          <select
            value={values.publicacion || 'todas'}
            onChange={e => onChange('publicacion', e.target.value)}
            className="bg-slate-900/60 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500"
          >
            {PUBLICACION_OPTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>
      )}
      {OPTIMIZATION_FILTERS.map(([field, label]) => (
        <label key={field} className="flex items-center gap-1 text-[10px] text-slate-500">
          {label}
          <select
            value={values[field]}
            onChange={e => onChange(field, e.target.value)}
            className="bg-slate-900/60 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500"
          >
            <option value="todas">Todas</option>
            <option value="optimizadas">Optimizadas</option>
            <option value="pendientes">Pendientes</option>
          </select>
        </label>
      ))}
    </div>
  );
}

function OptimizationBadges({ product }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {OPTIMIZATION_FIELDS.map(([field, label]) => {
        const done = Boolean(product[field]);
        return (
          <span key={field}
            title={done ? `${label}: optimizado` : `${label}: pendiente`}
            className={`text-[9px] px-1.5 py-0.5 rounded ${
              done ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-800 text-slate-500'}`}>
            {label}
          </span>
        );
      })}
    </div>
  );
}

// Every one is highest-first: the question a seller asks of their catalogue
// is always "which are the biggest".
const SORT_OPTIONS = [
  ['ficha', 'Ficha más vacía'],
  ['ventas', 'Más vendidas'],
  ['vistas', 'Más vistas'],
  ['calidad', 'Mejor calidad'],
];

function SortSelect({ value, onChange }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-500">
      Ordenar por
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="bg-slate-900/60 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-blue-500"
      >
        {SORT_OPTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
      </select>
    </label>
  );
}

// The numbers the sorting is done on, so a reordered list explains itself.
function SalesAndVisits({ product }) {
  const sales = product.sales_30d || 0;
  const visits = product.visits_60d || 0;
  if (!sales && !visits) return null;
  return (
    <span className="text-xs text-slate-500">
      {sales > 0 && `${sales} vendidas`}
      {sales > 0 && visits > 0 && ' · '}
      {visits > 0 && `${visits} visitas`}
    </span>
  );
}

function FilterRow({ options, value, onChange }) {
  return (
    <div className="flex bg-slate-900/60 border border-slate-800 rounded-lg p-0.5">
      {options.map(([id, label]) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            value === id ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function DataSheetTab() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('todas');
  const [stock, setStock] = useState('todas');
  const [marca, setMarca] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [optimizationFilters, setOptimizationFilters] = useState({ seo: 'todas', descripcion: 'todas', imagenes: 'todas', ficha: 'todas' });
  const [orden, setOrden] = useState('ficha');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [stepText, setStepText] = useState('');
  const [sheet, setSheet] = useState(null);
  const [chosen, setChosen] = useState({});
  const [showManual, setShowManual] = useState(false);
  const [applying, setApplying] = useState(false);
  const [destino, setDestino] = useState('yuju');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/attributes/pending', {
        params: { search, estado, stock, marca, proveedor, ...optimizationFilters, publicacion: 'publicadas', orden, page, limit: SHEET_PAGE_SIZE },
      });
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar tus publicaciones');
    } finally {
      setLoading(false);
    }
  }, [search, estado, stock, marca, proveedor, optimizationFilters, orden, page]);

  // Typing is not a search: waiting out the pause is what keeps one query
  // per word instead of one per keystroke.
  React.useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  // Any change to what is being asked for starts the results over; page 4 of
  // the old filter is not page 4 of the new one.
  React.useEffect(() => { setPage(1); }, [search, estado, stock, marca, proveedor, optimizationFilters, orden]);

  const pages = Math.max(1, Math.ceil(total / SHEET_PAGE_SIZE));
  const firstShown = total ? (page - 1) * SHEET_PAGE_SIZE + 1 : 0;
  const lastShown = Math.min(page * SHEET_PAGE_SIZE, total);

  const analyse = async (product) => {
    setActive(product);
    setSheet(null);
    setChosen({});
    setShowManual(false);
    setAnalysing(true);
    setStepText('Enviando...');
    try {
      const { data } = await api.post(`/attributes/suggest/${product.meli_id}`);
      const done = await pollJob('/attributes/job-status', data.job_id, setStepText);
      setSheet(done);
      // Everything the model is sure about starts ticked; the rest is a
      // deliberate choice, because a wrong attribute is worse than a gap.
      setChosen(Object.fromEntries(
        (done.sugerencias || []).map(sug => [sug.id, sug.confianza === 'alta'])));
      if (!(done.sugerencias || []).length) toast.info('No hay atributos que proponer aquí');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error al analizar la ficha');
    } finally {
      setAnalysing(false);
      setStepText('');
    }
  };

  const setValue = (id, value) =>
    setSheet(s => ({
      ...s,
      sugerencias: s.sugerencias.map(sug => (sug.id === id ? { ...sug, value_name: value } : sug)),
    }));

  const apply = async () => {
    const attributes = (sheet.sugerencias || [])
      .filter(sug => chosen[sug.id] && sug.value_name?.trim())
      .map(sug => ({ id: sug.id, name: sug.nombre, value_name: sug.value_name.trim() }));

    if (!attributes.length) return toast.error('Marca al menos un atributo');
    setApplying(true);
    try {
      const { data } = await api.post(`/attributes/apply/${sheet.item_id}`, { attributes });
      toast.success(`${data.aplicados} atributos escritos en tu publicación`);
      setSheet(null);
      setActive(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mercado Libre rechazó el cambio');
    } finally {
      setApplying(false);
    }
  };

  // Two groups: what the AI filled in, and what only the seller can answer.
  const proposed = (sheet?.sugerencias || []).filter(sug => sug.confianza);
  const manual = (sheet?.sugerencias || []).filter(sug => !sug.confianza);

  // Count what apply() would actually send: a row ticked and then emptied
  // is not an attribute.
  const selected = (sheet?.sugerencias || [])
    .filter(sug => chosen[sug.id] && sug.value_name?.trim()).length;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-white mb-2">Ficha Técnica</h2>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto">
          Mercado Libre califica tus publicaciones por lo completa que esté su ficha.
          La IA propone los atributos que faltan; tú apruebas cuáles se escriben.
        </p>
      </div>

      {!sheet && (
        <div className="max-w-3xl mx-auto space-y-4">
          {/* A catalogue is reached by looking for one listing, not by
              scrolling past the other nine hundred. */}
          <div className="space-y-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Busca por SKU, MLM o título"
                className="w-full bg-slate-900/60 border border-slate-800 rounded-lg pl-9 pr-9 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-lg leading-none"
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              )}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <FilterRow options={SHEET_FILTERS.estado} value={estado} onChange={setEstado} />
              <FilterRow options={SHEET_FILTERS.stock} value={stock} onChange={setStock} />
              <OptimizationFilters values={optimizationFilters} onChange={(field, value) => setOptimizationFilters(f => ({ ...f, [field]: value }))} />
              <CatalogueFilters marca={marca} setMarca={setMarca}
                proveedor={proveedor} setProveedor={setProveedor} />
              <SortSelect value={orden} onChange={setOrden} />
            </div>
          </div>

          {loading ? (
            <p className="text-center text-sm text-slate-500 py-8">Cargando publicaciones...</p>
          ) : !products.length ? (
            <p className="text-center text-sm text-slate-500 py-8">
              {search || estado !== 'todas' || stock !== 'todas' || marca || proveedor || Object.values(optimizationFilters).some(v => v !== 'todas')
                ? 'Ninguna publicación coincide con esos filtros.'
                : 'No hay publicaciones sincronizadas. Corre una sincronización primero.'}
            </p>
          ) : (
            <>
              <p className="text-xs text-slate-500">
                Mostrando {firstShown}–{lastShown} de {total} publicaciones
              </p>
              <div className="space-y-2">
                {products.map(product => (
                  <div key={product.meli_id}
                       className="flex items-center gap-3 bg-slate-900/50 border border-slate-800/50 rounded-xl p-3">
                    {product.image && (
                      <img src={product.image} alt="" className="w-12 h-12 rounded object-cover bg-slate-800 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{product.name || product.meli_id}</p>
                      <ItemIds product={product} />
                      <div className="flex items-center gap-2 flex-wrap mt-0.5">
                        {/* The state and the stock are what the filters sort
                            on, so the row has to show them. */}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          product.status === 'active'
                            ? 'bg-emerald-500/15 text-emerald-400'
                            : 'bg-slate-700/50 text-slate-400'}`}>
                          {product.status === 'active' ? 'Activa' : 'Inactiva'}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          product.stock_total > 0
                            ? 'bg-slate-700/50 text-slate-300'
                            : 'bg-red-500/15 text-red-400'}`}>
                          {product.stock_total > 0 ? `${product.stock_total} en stock` : 'Sin stock'}
                        </span>
                        <SalesAndVisits product={product} />
                        <span className="text-xs text-slate-500">
                          {product.atributos_faltantes
                            ? `${product.atributos_faltantes} atributos por completar`
                            : 'Sin revisar'}
                        </span>
                      </div>
                    </div>
                    <HealthScore salud={product.salud} />
                    <button
                      onClick={() => analyse(product)}
                      disabled={analysing}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium disabled:opacity-50 shrink-0"
                    >
                      {analysing && active?.meli_id === product.meli_id ? (stepText || 'Analizando...') : 'Completar'}
                    </button>
                  </div>
                ))}
              </div>

              {pages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    Anterior
                  </button>
                  <span className="text-xs text-slate-500">Página {page} de {pages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(pages, p + 1))}
                    disabled={page >= pages}
                    className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {sheet && (
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-white">{sheet.titulo}</h3>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <HealthBadge salud={sheet.salud} />
                <span className="text-xs text-slate-500">
                  {sheet.faltantes} atributos vacíos · {sheet.con_propuesta ?? sheet.sugerencias.length} con
                  propuesta de la IA
                </span>
              </div>
            </div>
            <button onClick={() => { setSheet(null); setActive(null); }}
              className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800">
              Volver
            </button>
          </div>

          {!sheet.sugerencias.length ? (
            <p className="text-sm text-slate-400 text-center py-6">
              Esta publicación no tiene atributos vacíos que completar.
            </p>
          ) : (
            <>
              {/* What the AI could work out comes first and open; the rest
                  is real work for the seller and waits behind a click
                  instead of burying the part that is already done. */}
              <div className="space-y-2">
                {proposed.map(sug => (
                  <AttributeRow
                    key={sug.id}
                    sug={sug}
                    checked={!!chosen[sug.id]}
                    onToggle={value => setChosen(c => ({ ...c, [sug.id]: value }))}
                    onChange={value => setValue(sug.id, value)}
                  />
                ))}
                {!proposed.length && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    La IA no pudo deducir ningún atributo con los datos de esta publicación.
                    Prefiere dejarlo vacío antes que escribir un dato equivocado.
                  </p>
                )}
              </div>

              {manual.length > 0 && (
                <div className="rounded-xl border border-slate-800/50 bg-slate-900/30 overflow-hidden">
                  <button
                    onClick={() => setShowManual(v => !v)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-800/40 transition-colors"
                  >
                    <span className="text-sm text-slate-300">
                      {manual.length} atributo{manual.length === 1 ? '' : 's'} que la IA no pudo deducir
                      <span className="block text-xs text-slate-500 mt-0.5">
                        Complétalos tú para subir la calidad de la publicación
                      </span>
                    </span>
                    <span className="text-xs text-blue-400 shrink-0">
                      {showManual ? 'Ocultar ▲' : 'Mostrar ▼'}
                    </span>
                  </button>
                  {showManual && (
                    <div className="space-y-2 p-3 pt-0">
                      {manual.map(sug => (
                        <AttributeRow
                          key={sug.id}
                          sug={sug}
                          checked={!!chosen[sug.id]}
                          onToggle={value => setChosen(c => ({ ...c, [sug.id]: value }))}
                          onChange={value => setValue(sug.id, value)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-300">
                  Al aplicar, estos atributos se escriben en tu publicación real de Mercado Libre.
                  Revisa los valores: sólo se envía lo que dejes marcado.
                </p>
              </div>

              <button
                onClick={apply}
                disabled={applying || !selected}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
              >
                {applying ? 'Aplicando...' : `Aplicar ${selected} atributo${selected === 1 ? '' : 's'} a la publicación`}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// One page is one batch: the backend refuses more than fifty per run.
const BULK_PAGE_SIZE = 50;
// Mercado Libre cuts anything longer without saying so, and the apply step
// cuts it too, so an edited title over the limit is stopped before it is sent.
const MELI_TITLE_MAX = 60;
// Mirror BATCH_COMPLETA_MAX in publications.py; the server is authoritative.
const BATCH_COMPLETA_MAX = 20;

const BULK_TASKS = [
  { id: 'seo', label: 'Optimizador SEO', hint: 'Título de 60 caracteres y keywords' },
  { id: 'descripcion', label: 'Descripción', hint: 'Bloque completo listo para publicar' },
  { id: 'ficha', label: 'Ficha Técnica', hint: 'Atributos que Mercado Libre califica' },
];

function BulkTab() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [estado, setEstado] = useState('todas');
  const [stock, setStock] = useState('todas');
  const [marca, setMarca] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [optimizationFilters, setOptimizationFilters] = useState({ seo: 'todas', descripcion: 'todas', imagenes: 'todas', ficha: 'todas', publicacion: 'todas' });
  const [listaId, setListaId] = useState('');
  const [orden, setOrden] = useState('ficha');
  const [picked, setPicked] = useState({});
  const [tasks, setTasks] = useState({ seo: true, descripcion: false, ficha: false });
  const tasksRef = useRef(null);
  const [costs, setCosts] = useState({});

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, step: '' });
  const [results, setResults] = useState(null);
  const [approved, setApproved] = useState({});
  // What the seller rewrote, kept apart from what the AI proposed so the
  // proposal is still there to go back to.
  const [edits, setEdits] = useState({});
  const [applying, setApplying] = useState(false);
  const [destino, setDestino] = useState('yuju');
  const [jobId, setJobId] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [applyFailures, setApplyFailures] = useState([]);
  const [rejectedAttrs, setRejectedAttrs] = useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bulk/products', {
        params: { search: query, estado, stock, marca, proveedor, ...optimizationFilters, lista_id: listaId || undefined, orden, page, limit: BULK_PAGE_SIZE },
      });
      setProducts(data.products || []);
      setTotal(data.total || 0);
      setCosts(data.costos_por_item || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar tus publicaciones');
    } finally {
      setLoading(false);
    }
  }, [query, estado, stock, marca, proveedor, optimizationFilters, listaId, orden, page]);

  // Typing is not a search: waiting out the pause keeps it to one query per
  // word rather than one per keystroke.
  React.useEffect(() => {
    const timer = setTimeout(load, query ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, query]);

  React.useEffect(() => { setPage(1); }, [query, estado, stock, marca, proveedor, optimizationFilters, listaId, orden]);

  const loadRecentJobs = React.useCallback(async () => {
    try {
      const { data } = await api.get('/bulk/recent-jobs');
      setRecentJobs(data.jobs || []);
    } catch { /* the optimizer remains usable if this index is unavailable */ }
  }, []);
  React.useEffect(() => { loadRecentJobs(); }, [loadRecentJobs]);

  const openRecentJob = async id => {
    try {
      const { data } = await api.get(`/bulk/job-status/${id}`);
      if (data.status !== 'completed' || !data.result?.items) {
        return toast.error('Esta optimización ya no conserva resultados completos');
      }
      setJobId(id);
      setResults(data.result);
      setApproved({});
      setEdits({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo abrir la optimización');
    }
  };

  const visible = products;
  const pages = Math.max(1, Math.ceil(total / BULK_PAGE_SIZE));
  const firstShown = total ? (page - 1) * BULK_PAGE_SIZE + 1 : 0;
  const lastShown = Math.min(page * BULK_PAGE_SIZE, total);
  const chosenIds = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
  const chosenTasks = Object.entries(tasks).filter(([, v]) => v).map(([k]) => k);
  // PriceHub-only products have no ML listing to write to.
  const hasUnpublished = chosenIds.some(id => String(id).startsWith('SINPUB-'));
  React.useEffect(() => { if (hasUnpublished) setDestino('yuju'); }, [hasUnpublished]);
  const estimate = (chosenIds.length * chosenTasks.reduce((sum, t) => sum + (costs[t] || 0), 0)).toFixed(2);

  const toggleAllVisible = () => {
    const allPicked = visible.every(p => picked[p.meli_id]);
    const next = { ...picked };
    visible.forEach(p => { next[p.meli_id] = !allPicked; });
    setPicked(next);
  };

  const run = async () => {
    if (!chosenIds.length) return toast.error('Selecciona al menos una publicación');
    if (!chosenTasks.length) {
      tasksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return toast.error('Elige al menos una optimización');
    }
    if (chosenIds.length > BULK_PAGE_SIZE) {
      return toast.error(`Máximo ${BULK_PAGE_SIZE} publicaciones por tanda. Llevas ${chosenIds.length} seleccionadas.`);
    }

    setRunning(true);
    setResults(null);
    setProgress({ done: 0, total: chosenIds.length, step: 'En cola...' });
    try {
      const { data } = await api.post('/bulk/start', { item_ids: chosenIds, tasks: chosenTasks });
      setJobId(data.job_id);
      const done = await pollJob('/bulk/job-status', data.job_id, null, job =>
        setProgress({ done: job.procesados || 0, total: job.total || chosenIds.length, step: job.step || '' }),
        MAX_BATCH_POLL_MS);
      setResults(done);
      // Nothing is pre-approved: these rewrite live listings.
      setApproved({});
      setEdits({});
      setApplyFailures([]);
      setRejectedAttrs({});
      toast.success(`${done.items.length} publicaciones optimizadas`);
      loadRecentJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error en la optimización masiva');
    } finally {
      setRunning(false);
    }
  };

  const approve = React.useCallback((itemId, field, value) =>
    setApproved(a => ({ ...a, [itemId]: { ...(a[itemId] || {}), [field]: value } })), []);

  // Rewriting a field is an approval in itself: nobody corrects a title to
  // then leave it unchecked.
  const edit = React.useCallback((itemId, field, value) => {
    setEdits(e => ({ ...e, [itemId]: { ...(e[itemId] || {}), [field]: value } }));
    approve(itemId, field, true);
  }, [approve]);

  const revert = React.useCallback((itemId, field) => setEdits(e => {
    const row = { ...(e[itemId] || {}) };
    delete row[field];
    return { ...e, [itemId]: row };
  }), []);

  // What is on screen right now, which is what gets applied.
  const finalTitle = row => (edits[row.item_id]?.titulo ?? row.titulo_propuesto ?? '').trim();
  const finalDescription = row =>
    edits[row.item_id]?.descripcion ?? row.descripcion?.description ?? '';

  // Approved sheet values as they stand on screen, edits included.
  const finalAttributes = (row, flags) => (row.ficha?.sugerencias || [])
    .filter(sug => flags[`attr:${sug.id}`])
    .map(sug => ({
      id: sug.id,
      value_name: String(edits[row.item_id]?.[`attr:${sug.id}`] ?? sug.value_name ?? '').trim(),
    }))
    .filter(attr => attr.value_name);

  // Approved text that Mercado Libre would reject or silently truncate.
  const blockedReasons = (results?.items || []).flatMap(row => {
    const flags = approved[row.item_id] || {};
    const reasons = [];
    const titulo = finalTitle(row);
    if (flags.titulo && !titulo) reasons.push('título vacío');
    if (flags.titulo && titulo.length > MELI_TITLE_MAX) reasons.push(`título de más de ${MELI_TITLE_MAX} caracteres`);
    if (flags.descripcion && !finalDescription(row).trim()) reasons.push('descripción vacía');
    return reasons.length ? [{ row, reasons }] : [];
  });
  const blocked = blockedReasons.map(({ row }) => row);

  const apply = async () => {
    if (blocked.length) {
      return toast.error(
        `${blocked.length} publicación(es) con un campo vacío o un título de más de ${MELI_TITLE_MAX} caracteres`);
    }
    const items = (results?.items || []).map(row => {
      const flags = approved[row.item_id] || {};
      const payload = { item_id: row.item_id, sku: row.seller_sku, family_name: row.family_name };
      const titulo = finalTitle(row);
      const descripcion = finalDescription(row);
      if (flags.titulo && titulo) payload.titulo = titulo;
      if (flags.descripcion && descripcion.trim()) payload.descripcion = descripcion;
      const atributos = finalAttributes(row, flags);
      if (atributos.length) payload.atributos = atributos;
      return payload;
    }).filter(p => p.titulo || p.descripcion || p.atributos?.length);

    if (!items.length) return toast.error('No aprobaste ningún cambio');
    setApplying(true);
    try {
      const { data } = await api.post('/bulk/apply', { items, destino, job_id: jobId });
      if (data.en_cola_yuju) {
        // Try the handoff immediately. If Yuju is disconnected or its
        // catalogue is temporarily unavailable, the durable queue remains
        // visible in Configuración for a later retry.
        try {
          const pushed = await api.post('/yuju/push-content', {});
          if (pushed.data.enviados) {
            toast.success(`${pushed.data.enviados} enviadas a Yuju`);
          }
          if (pushed.data.fallidos?.length) {
            toast.error(`${pushed.data.fallidos.length} quedaron pendientes en Yuju`);
          }
        } catch {
          toast.info(`${data.en_cola_yuju} quedaron en cola para Yuju; reintenta desde Configuración`);
        }
      }
      if (data.aplicados) toast.success(`${data.aplicados} escritas en Mercado Libre`);
      if (data.fallidos?.length) {
        setApplyFailures(data.fallidos);
        const nextRejected = {};
        data.fallidos.forEach(failure => {
          if (failure.atributos_rechazados?.length) {
            nextRejected[failure.item_id] = failure.atributos_rechazados;
          }
        });
        setRejectedAttrs(nextRejected);
        setApproved(prev => {
          const next = { ...prev };
          Object.entries(nextRejected).forEach(([itemId, ids]) => {
            const flags = { ...(next[itemId] || {}) };
            ids.forEach(id => { delete flags[`attr:${id}`]; });
            next[itemId] = flags;
          });
          return next;
        });
        toast.error(`${data.fallidos.length} cambios rechazados por Mercado Libre`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aplicar los cambios');
    } finally {
      setApplying(false);
    }
  };

  const approvedCount = Object.values(approved)
    .reduce((sum, flags) => sum + Object.values(flags).filter(Boolean).length, 0);

  // What each listing actually came back with, so "approve everything" means
  // everything there is rather than every checkbox that could exist.
  const proposals = (results?.items || []).map(row => [row.item_id, {
    titulo: Boolean(row.titulo_propuesto),
    descripcion: Boolean(row.descripcion?.description),
    ...Object.fromEntries((row.ficha?.sugerencias || [])
      .filter(sug => sug.confianza !== 'manual'
        && !rejectedAttrs[row.item_id]?.includes(sug.id))
      .map(sug => [`attr:${sug.id}`, true])),
  }]);
  const proposedCount = proposals
    .reduce((sum, [, flags]) => sum + Object.values(flags).filter(Boolean).length, 0);
  const allApproved = proposedCount > 0 && approvedCount === proposedCount;

  const approveAll = () =>
    setApproved(allApproved ? {} : Object.fromEntries(
      proposals.map(([id, f]) => [id, { ...f }])));

  // Tick or untick one confidence level of the sheet across every listing.
  const levelKeys = (row, level) => (row.ficha?.sugerencias || [])
    .filter(sug => sug.confianza === level && !rejectedAttrs[row.item_id]?.includes(sug.id))
    .map(sug => `attr:${sug.id}`);
  const levelRows = (level) => (results?.items || []).filter(row => levelKeys(row, level).length);
  const levelAllOn = (level) => {
    const rows = levelRows(level);
    return rows.length > 0 && rows.every(row =>
      levelKeys(row, level).every(key => approved[row.item_id]?.[key]));
  };
  const toggleLevel = (level) => {
    const on = !levelAllOn(level);
    setApproved(prev => {
      const next = { ...prev };
      levelRows(level).forEach(row => {
        const flags = { ...(next[row.item_id] || {}) };
        levelKeys(row, level).forEach(key => { flags[key] = on; });
        next[row.item_id] = flags;
      });
      return next;
    });
  };

  // Only titles and descriptions have a destination to choose. A run that
  // only touched the sheet has nothing to ask about: attributes can only go
  // to Mercado Libre.
  const textGoesSomewhere = proposals.some(([, f]) => f.titulo || f.descripcion);

  const downloadExcel = async () => {
    try {
      const exportItems = (results?.items || []).map(row => {
        const item = { item_id: row.item_id };
        const titulo = finalTitle(row);
        const descripcion = finalDescription(row);
        if (titulo) item.titulo = titulo;
        if (descripcion) item.descripcion = descripcion;
        if (row.ficha?.sugerencias?.length) item.atributos = row.ficha.sugerencias;
        return item;
      });
      const { data } = await api.post(`/bulk/export/${jobId}.xlsx`, { items: exportItems }, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'optimizacion-masiva.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo armar el Excel');
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-white mb-2">Optimización Masiva</h2>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto">
          Elige publicaciones y qué optimizar. Verás cada resultado antes de que toque
          tus publicaciones reales.
        </p>
      </div>

      {!results && (
        <>
          <div ref={tasksRef} className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3">
            {BULK_TASKS.map(task => (
              <button
                key={task.id}
                onClick={() => setTasks(t => ({ ...t, [task.id]: !t[task.id] }))}
                className={`text-left p-4 rounded-xl border transition-colors ${
                  tasks[task.id] ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600'}`}
              >
                <p className="text-sm font-semibold text-white">{task.label}</p>
                <p className="text-xs text-slate-400 mt-1">{task.hint}</p>
              </button>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500">
            Las imágenes van por su cuenta: Generador Imágenes → "Por lotes", hasta 50
            publicaciones por tanda.
          </p>

          <div className="max-w-3xl mx-auto space-y-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Busca por SKU, MLM, título o marca"
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg pl-9 pr-9 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-lg leading-none"
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <FilterRow options={SHEET_FILTERS.estado} value={estado} onChange={setEstado} />
              <FilterRow options={SHEET_FILTERS.stock} value={stock} onChange={setStock} />
              <OptimizationFilters conPublicacion values={optimizationFilters} onChange={(field, value) => setOptimizationFilters(f => ({ ...f, [field]: value }))} />
              <CatalogueFilters marca={marca} setMarca={setMarca}
              proveedor={proveedor} setProveedor={setProveedor} />
            <ListaFiltro listaId={listaId} setListaId={setListaId} />
              <SortSelect value={orden} onChange={setOrden} />
              <button onClick={toggleAllVisible}
                className="px-3 py-2 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800">
                {visible.every(p => picked[p.meli_id]) && visible.length ? 'Quitar esta página' : 'Seleccionar esta página'}
              </button>
            </div>

            {loading ? (
              <p className="text-center text-sm text-slate-500 py-8">Cargando publicaciones...</p>
            ) : !visible.length ? (
              <p className="text-center text-sm text-slate-500 py-8">
              {query || estado !== 'todas' || stock !== 'todas' || marca || proveedor || listaId || Object.values(optimizationFilters).some(v => v !== 'todas')
                  ? 'Ninguna publicación coincide con esos filtros.'
                  : 'No hay publicaciones. Corre una sincronización primero.'}
              </p>
            ) : (
              <>
              <p className="text-xs text-slate-500">
                Mostrando {firstShown}–{lastShown} de {total} publicaciones
                {chosenIds.length > 0 && (
                  <span className="text-blue-400"> · {chosenIds.length} seleccionadas en total</span>
                )}
              </p>
              <div className="max-h-96 overflow-y-auto space-y-1.5 pr-1">
                {visible.map(product => (
                  <label key={product.meli_id}
                    className={`flex items-center gap-3 rounded-lg p-2.5 border cursor-pointer ${
                      picked[product.meli_id] ? 'border-blue-500/50 bg-blue-500/5' : 'border-slate-800/50 bg-slate-900/50'}`}>
                    <input
                      type="checkbox"
                      checked={!!picked[product.meli_id]}
                      onChange={e => setPicked(p => ({ ...p, [product.meli_id]: e.target.checked }))}
                      className="accent-blue-500 shrink-0"
                    />
                    {product.image && (
                      <img src={product.image} alt="" className="w-10 h-10 rounded object-cover bg-slate-800 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white truncate">{product.name || product.meli_id}</p>
                      <ItemIds product={product} />
                      <div className="flex items-center gap-2 flex-wrap">
                        {product.sin_publicar ? (
                          <span title="Producto de PriceHub sin publicación en Mercado Libre: optimízalo y publícalo después"
                            className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
                            Sin publicar
                          </span>
                        ) : (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            product.status === 'active'
                              ? 'bg-emerald-500/15 text-emerald-400'
                              : 'bg-slate-700/50 text-slate-400'}`}>
                            {product.status === 'active' ? 'Activa' : 'Inactiva'}
                          </span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          product.stock_total > 0
                            ? 'bg-slate-700/50 text-slate-300'
                            : 'bg-red-500/15 text-red-400'}`}>
                          {product.stock_total > 0 ? `${product.stock_total} en stock` : 'Sin stock'}
                        </span>
                        {product.marca && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-300">
                            {product.marca}
                          </span>
                        )}
                        <SalesAndVisits product={product} />
                        <span className="text-xs text-slate-500">
                          {product.titulo_chars}/60 caracteres
                          {product.titulo_corto && <span className="text-amber-400"> · título corto</span>}
                          {product.atributos_faltantes > 0 && ` · ${product.atributos_faltantes} atributos vacíos`}
                        </span>
                      </div>
                      <OptimizationBadges product={product} />
                    </div>
                    <HealthScore salud={product.salud} />
                  </label>
                ))}
              </div>

              {pages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    Anterior
                  </button>
                  <span className="text-xs text-slate-500">Página {page} de {pages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(pages, p + 1))}
                    disabled={page >= pages}
                    className="px-3 py-1.5 border border-slate-700 text-slate-300 rounded-lg text-xs hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    Siguiente
                  </button>
                </div>
              )}
              </>
            )}
          </div>

          <div className="max-w-3xl mx-auto bg-slate-900/50 border border-slate-800/50 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm text-white">
                <span className={chosenIds.length > BULK_PAGE_SIZE ? 'text-red-400' : ''}>
                  {chosenIds.length} publicaciones
                </span>
                {' · '}{chosenTasks.length} optimizaciones
                {chosenTasks.length === 0 && (
                  <span className="block text-xs text-amber-400 mt-0.5">
                    Elige arriba qué optimizar (SEO, Descripción o Ficha)
                  </span>
                )}
                {chosenIds.length > BULK_PAGE_SIZE && (
                  <span className="block text-xs text-red-400 mt-0.5">
                    Máximo {BULK_PAGE_SIZE} por tanda: quita {chosenIds.length - BULK_PAGE_SIZE}
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-500">
                Costo estimado en OpenAI: <span className="text-emerald-400">${estimate} USD</span> · se cobra en tu cuenta
              </p>
            </div>
            <button
              onClick={run}
              disabled={running || !chosenIds.length
                        || chosenIds.length > BULK_PAGE_SIZE}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50"
            >
              {running ? 'Optimizando...' : `Optimizar ${chosenIds.length}`}
            </button>
          </div>

          {running && (
            <div className="max-w-lg mx-auto">
              <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all"
                     style={{ width: `${(progress.done / (progress.total || 1)) * 100}%` }} />
              </div>
              <p className="text-center text-xs text-slate-400 mt-2">{progress.step}</p>
            </div>
          )}

          {recentJobs.length > 0 && (
            <div className="max-w-3xl mx-auto rounded-xl border border-slate-800/50 bg-slate-900/50 p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-xs text-slate-400">Resultados recientes</p>
                <button onClick={loadRecentJobs} className="text-xs text-blue-400 hover:text-blue-300">Actualizar</button>
              </div>
              <div className="space-y-1.5">
                {recentJobs.map(job => (
                  <button key={job.job_id} onClick={() => openRecentJob(job.job_id)}
                    className="w-full flex items-center gap-3 rounded-lg border border-slate-800 px-3 py-2 text-left hover:border-slate-600">
                    <span className="text-xs text-slate-300 flex-1">
                      {job.total || 0} publicaciones · {(job.tasks || []).join(', ')}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {job.finished_at && new Date(job.finished_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className={`text-[10px] ${job.aplicado ? 'text-emerald-400' : 'text-amber-300'}`}>
                      {job.aplicado ? 'Ya aplicado' : 'Sin aplicar'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {results && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-bold text-white">{results.items.length} publicaciones optimizadas</h3>
              <div className="flex items-center gap-2 flex-wrap mt-0.5 text-xs">
                <button onClick={approveAll} className="text-blue-400 hover:text-blue-300 underline">
                  {allApproved ? 'Quitar todas las aprobaciones' : `Seleccionar todo (${proposedCount})`}
                </button>
                {FICHA_LEVELS.map(level => levelRows(level.key).length > 0 && (
                  <button key={level.key} onClick={() => toggleLevel(level.key)}
                    className={`px-2 py-0.5 rounded border ${levelAllOn(level.key)
                      ? 'border-blue-500 text-blue-300 bg-blue-500/10'
                      : 'border-slate-700 text-slate-400 hover:border-slate-600'}`}>
                    Ficha {level.key} ({levelRows(level.key).length})
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={downloadExcel}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-800">
                Exportar Excel
              </button>
              <button onClick={() => { setResults(null); setApproved({}); setEdits({}); }}
                className="px-4 py-2 border border-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-800">
                Volver
              </button>
              <button onClick={apply} disabled={applying || !approvedCount || blocked.length > 0}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {applying ? 'Aplicando...' : `Aplicar ${approvedCount} cambios`}
              </button>
            </div>
          </div>

          {blocked.length > 0 && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200">
              <p className="font-medium">No se pueden aplicar {blocked.length} publicaciones:</p>
              <ul className="mt-1 space-y-0.5">
                {blockedReasons.map(({ row, reasons }) => <li key={row.item_id}>{row.item_id}: {reasons.join(' y ')}</li>)}
              </ul>
              <p className="mt-1 text-red-300/80">Corrige esos campos para continuar. Los atributos vacíos se descartan automáticamente.</p>
            </div>
          )}

          {applyFailures.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-red-300">Cambios que Mercado Libre no aplicó</h4>
                  <p className="text-xs text-red-200/70 mt-1">Revisa el detalle completo por publicación.</p>
                </div>
                <button onClick={() => setApplyFailures([])} aria-label="Cerrar errores"
                  className="text-red-300 hover:text-white"><X size={16} /></button>
              </div>
              <div className="space-y-3">
                {applyFailures.map(failure => {
                  const row = results.items.find(candidate => candidate.item_id === failure.item_id);
                  return (
                    <div key={failure.item_id} className="rounded-lg bg-slate-950/40 p-3">
                      <p className="text-xs font-medium text-white">{failure.item_id}
                        {row?.titulo_actual ? <span className="text-slate-400 font-normal"> · {row.titulo_actual}</span> : null}
                      </p>
                      <ul className="mt-1 space-y-1 text-xs text-red-200">
                        {(failure.problemas || []).map((problem, index) => <li key={`${failure.item_id}-${index}`}>{problem}</li>)}
                      </ul>
                      {failure.atributos_rechazados?.length > 0 && (
                        <p className="mt-2 text-[11px] text-amber-300">
                          Atributos rechazados y excluidos de “Seleccionar todo”: {failure.atributos_rechazados.join(', ')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {textGoesSomewhere ? (
          <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
            <p className="text-xs text-slate-400 mb-2">¿A dónde van títulos y descripciones?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => setDestino('yuju')}
                className={`text-left p-3 rounded-lg border text-xs transition-colors ${
                  destino === 'yuju' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600'}`}>
                <p className="font-semibold text-white">Yuju</p>
                <p className="text-slate-400 mt-0.5">
                  Actualiza el título y la descripción generales del producto en Yuju.
                  Si falla el envío, puedes reintentarlo desde Configuración.
                </p>
              </button>
              <button onClick={() => setDestino('meli')} disabled={hasUnpublished}
                title={hasUnpublished ? 'Hay productos sin publicar seleccionados: solo pueden ir a Yuju' : undefined}
                className={`text-left p-3 rounded-lg border text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  destino === 'meli' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600'}`}>
                <p className="font-semibold text-white">Mercado Libre directo</p>
                <p className="text-slate-400 mt-0.5">
                  Se escriben de inmediato. Si Yuju también las administra, puede pisarlas.
                </p>
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              La ficha técnica siempre va directo a Mercado Libre: la API de Yuju no escribe atributos.
              {hasUnpublished && (
                <span className="block text-amber-400 mt-1">
                  Los productos sin publicar solo envían título y descripción a Yuju; su ficha técnica
                  queda guardada hasta que exista la publicación.
                </span>
              )}
            </p>
          </div>
          ) : (
            <p className="text-xs text-slate-500 text-center">
              Los atributos se escriben directo en Mercado Libre: la API de Yuju no los acepta.
            </p>
          )}

          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-300">
              Sólo se escribe lo que marques. Nada está aprobado de antemano: esto reemplaza
              el título y la descripción de publicaciones vivas. Puedes corregir el texto
              antes de aplicarlo; se aplica lo que quede en pantalla.
            </p>
          </div>

          <div className="space-y-3">
            {results.items.map(row => (
              <BulkRow key={row.item_id} row={row} flags={approved[row.item_id]}
                       edits={edits[row.item_id]} onApprove={approve}
                       onEdit={edit} onRevert={revert} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const FICHA_LEVELS = [{ key: 'alta' }, { key: 'media' }, { key: 'baja' }];
// SAT keys: listed empty, filled by hand; never part of select-all.
const MANUAL_LEVEL = { key: 'manual' };

const BulkRow = React.memo(function BulkRow({ row, flags = {}, edits = {}, onApprove, onEdit, onRevert }) {
  const [open, setOpen] = useState(false);
  const fichaByLevel = [...FICHA_LEVELS, MANUAL_LEVEL].map(l => ({
    ...l, items: (row.ficha?.sugerencias || []).filter(s => s.confianza === l.key),
  })).filter(l => l.items.length);

  const titulo = edits.titulo ?? row.titulo_propuesto ?? '';
  const descripcion = edits.descripcion ?? row.descripcion?.description ?? '';
  const tituloEditado = edits.titulo !== undefined && edits.titulo !== row.titulo_propuesto;
  const descripcionEditada = edits.descripcion !== undefined
    && edits.descripcion !== row.descripcion?.description;
  const tituloLargo = titulo.trim().length > MELI_TITLE_MAX;

  return (
    <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
      <div className="flex items-start gap-3">
        {row.imagen && <img src={row.imagen} alt="" className="w-12 h-12 rounded object-cover bg-slate-800 shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-sm text-slate-400 truncate">{row.titulo_actual}</p>
          <ItemIds product={row} className="mt-0.5" />
          {row.errores?.length > 0 && (
            <p className="text-xs text-red-400 mt-1">{row.errores.join(' · ')}</p>
          )}

          {row.titulo_propuesto && (
            <div className="mt-2">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={!!flags.titulo}
                  onChange={e => onApprove(row.item_id, 'titulo', e.target.checked)}
                  className="accent-emerald-500 shrink-0" />
                <input
                  type="text"
                  value={titulo}
                  onChange={e => onEdit(row.item_id, 'titulo', e.target.value)}
                  className={`flex-1 min-w-0 bg-slate-950/60 border rounded-lg px-2.5 py-1.5 text-sm text-white focus:outline-none ${
                    tituloLargo ? 'border-red-500/60 focus:border-red-500' : 'border-slate-800 focus:border-blue-500'}`}
                />
              </div>
              <div className="flex items-center flex-wrap gap-3 mt-1 pl-6">
                <span className={`text-xs ${tituloLargo ? 'text-red-400' : 'text-slate-500'}`}>
                  {titulo.trim().length}/{MELI_TITLE_MAX}
                </span>
                {tituloLargo && (
                  <span className="text-xs text-red-400">
                    Mercado Libre no acepta más de {MELI_TITLE_MAX} caracteres.
                  </span>
                )}
                {tituloEditado && (
                  <button onClick={() => onRevert(row.item_id, 'titulo')}
                    className="text-xs text-blue-400 hover:text-blue-300 underline">
                    volver a la propuesta
                  </button>
                )}
              </div>
            </div>
          )}

          {row.descripcion?.description && (
            <div className="mt-2">
              <div className="flex items-center flex-wrap gap-2">
                <input type="checkbox" checked={!!flags.descripcion}
                  onChange={e => onApprove(row.item_id, 'descripcion', e.target.checked)}
                  className="accent-emerald-500 shrink-0" />
                <span className="text-sm text-slate-300">
                  Descripción nueva ({descripcion.length} caracteres)
                </span>
                <button onClick={() => setOpen(!open)}
                  className="text-xs text-blue-400 hover:text-blue-300">
                  {open ? 'ocultar' : 'ver y editar'}
                </button>
                {descripcionEditada && (
                  <button onClick={() => onRevert(row.item_id, 'descripcion')}
                    className="text-xs text-blue-400 hover:text-blue-300 underline">
                    volver a la propuesta
                  </button>
                )}
                {!descripcion.trim() && (
                  <span className="text-xs text-red-400">Vacía: no hay nada que aplicar.</span>
                )}
              </div>
              {open && (
                <textarea
                  value={descripcion}
                  onChange={e => onEdit(row.item_id, 'descripcion', e.target.value)}
                  rows={14}
                  className="mt-2 w-full bg-slate-950/60 border border-slate-800 rounded-lg p-3 text-[11px] text-slate-300 font-mono focus:outline-none focus:border-blue-500"
                />
              )}
            </div>
          )}

          {fichaByLevel.map(level => (
            <div key={level.key} className="mt-3">
              {level.key === 'manual' ? (
                <p className="text-sm text-slate-300 mb-1">
                  Por llenar a mano (claves SAT) · se aplican al escribir un valor
                </p>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer mb-1">
                  <input type="checkbox"
                    checked={level.items.every(sug => !!flags[`attr:${sug.id}`])}
                    onChange={e => level.items.forEach(sug =>
                      onApprove(row.item_id, `attr:${sug.id}`, e.target.checked))}
                    className="accent-emerald-500 shrink-0" />
                  <span className="text-sm text-slate-300">
                    {level.items.length} atributo{level.items.length === 1 ? '' : 's'} de confianza {level.key}
                  </span>
                </label>
              )}
              <div className="space-y-1.5 pl-6">
                {level.items.map(sug => {
                  const key = `attr:${sug.id}`;
                  const value = edits[key] ?? sug.value_name ?? '';
                  const options = sug.opciones || [];
                  return (
                    <div key={sug.id} className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-44 shrink-0 truncate" title={sug.nombre}>
                        {sug.nombre}
                      </span>
                      {options.length > 0 ? (
                        <select value={value} onChange={e => onEdit(row.item_id, key, e.target.value)}
                          className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white">
                          {!options.includes(value) && <option value={value}>{value || '—'}</option>}
                          {options.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input type="text" value={value}
                          onChange={e => onEdit(row.item_id, key, e.target.value)}
                          className="flex-1 min-w-0 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white" />
                      )}
                      {edits[key] !== undefined && edits[key] !== sug.value_name && (
                        <button onClick={() => onRevert(row.item_id, key)}
                          className="text-xs text-slate-500 hover:text-slate-300 shrink-0">Restaurar</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default function PublicationsPage() {
  const [activeTab, setActiveTab] = useState('images');
  // Held at this level so the description tab can reuse the SEO analysis
  // instead of making the user describe the product a second time.
  const [seoAnalysis, setSeoAnalysis] = useState(null);

  const tabs = [
    { id: 'images', label: 'Generador Imágenes', icon: Image },
    { id: 'seo', label: 'Optimizador SEO', icon: Search },
    { id: 'description', label: 'Descripción', icon: FileText },
    { id: 'ficha', label: 'Ficha Técnica', icon: LayoutDashboard },
    { id: 'masivo', label: 'Masivo', icon: TrendingUp },
  ];

  return (
    <DashboardLayout
      title="Construcción de Publicaciones"
      icon={BookOpen}
      backTo="/dashboard"
      headerRight={
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-3 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800">
            <PlayCircle size={16} />
            Ver tutorial
          </button>
          <a href="/dashboard" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors">
            <LayoutDashboard size={15} />
            Dashboard
          </a>
          <a href="/profitability" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors">
            <TrendingUp size={15} />
            Rentabilidad
          </a>
        </div>
      }
    >
      {/* Tabs */}
      <div className="flex items-center justify-center gap-1 bg-slate-900/50 border border-slate-800/50 rounded-xl p-1 w-fit mx-auto mb-8">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <tab.icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'images' && <ImageGeneratorTab seoAnalysis={seoAnalysis} onGoToSeo={() => setActiveTab('seo')} />}
      {activeTab === 'seo' && (
        <SEOOptimizerTab
          onAnalysis={a => setSeoAnalysis(a && { ...a, analizado_en: Date.now() })}
        />
      )}
      {activeTab === 'description' && <DescriptionTab seoAnalysis={seoAnalysis} onGoToSeo={() => setActiveTab('seo')} />}
      {activeTab === 'ficha' && <DataSheetTab />}
      {activeTab === 'masivo' && <BulkTab />}
    </DashboardLayout>
  );
}
